import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ClientAddForm from './ClientAddForm';
import ClientEditForm from './ClientEditForm';
import { getClientNomComplet } from '../utils/clientNom';
import { toCalendarYmd } from '../utils/dateYmd';
    import {
    getActiveCaisseYear,
    setActiveCaisseYear,
    buildCaisseYearOptions,
    subscribeCaisseYear,
    syncActiveCaisseYearWithCalendar,
    getCaisseStartYear,
    getCalendarYear,
} from '../utils/caisseActiveYear';
import { authJsonHeaders } from '../utils/authHeaders';
import './NHPage.css';
import './CgmFilterToolbar.css';
const PERIODE_LABELS = {
    mensuel: 'Mensuel',
    trimestriel: 'Trimestriel',
    semestriel: 'Semestriel',
    annuel: 'Annuel'
};

const MOIS_HONO_OPTIONS = [
    { value: '1', label: '1er janvier' },
    { value: '2', label: '1er février' },
    { value: '3', label: '1er mars' },
    { value: '4', label: '1er avril' },
    { value: '5', label: '1er mai' },
    { value: '6', label: '1er juin' },
    { value: '7', label: '1er juillet' },
    { value: '8', label: '1er août' },
    { value: '9', label: '1er septembre' },
    { value: '10', label: '1er octobre' },
    { value: '11', label: '1er novembre' },
    { value: '12', label: '1er décembre' },
];

function mergeMontantsFromRows(rows) {
    const map = {};
    (rows || []).forEach((r) => {
        const id = r.client_id;
        if (!map[id]) map[id] = {};
        const v = parseFloat(String(r.montant).replace(',', '.'));
        map[id][r.periode_type] = Number.isNaN(v) ? null : v;
    });
    return map;
}

function mergeStartDatesFromRows(rows) {
    const map = {};
    (rows || []).forEach((r) => {
        const id = r.client_id;
        if (!map[id]) map[id] = {};
        map[id][r.periode_type] = toCalendarYmd(r.start_date);
    });
    return map;
}

function mergeStoppedFromRows(rows) {
    const map = {};
    (rows || []).forEach((r) => {
        const id = r.client_id;
        map[id] = {
            stopped: !!r.stopped,
            stop_date: toCalendarYmd(r.stop_date)
        };
    });
    return map;
}

/** Affichage colonne Montant Hono : toujours 3 décimales (ex. 100 → « 100,000 ») */
function formatMontantAffiche(v) {
    if (v == null || Number.isNaN(v)) return '';
    return Number(v).toLocaleString('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
    });
}

function getClientsPageNumbers(currentPage, totalPages) {
    const pageNumbers = [];
    const maxVisible = 6;

    if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pageNumbers.push(i);
    } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
    }
    return pageNumbers;
}

export default function AllClientsPage() {
    const { isAdmin, activeCabinet } = useAuth();
    const userIsAdmin = isAdmin();
    /** Admin système ou Admin du cabinet actif (y compris admin multi-cabinets) */
    const isCabinetAdmin =
        userIsAdmin || String(activeCabinet?.member_role || '') === 'Admin';
    const [allClients, setAllClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterNhType, setFilterNhType] = useState('');
    const [filterPeriode, setFilterPeriode] = useState('');
    const [filterPersonneType, setFilterPersonneType] = useState('');
    const [activeCaisseYear, setActiveCaisseYearState] = useState(() =>
        syncActiveCaisseYearWithCalendar()
    );
    const caisseYearOptions = useMemo(() => buildCaisseYearOptions(), [activeCaisseYear]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteMode, setDeleteMode] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    /** Par client : période choisie parmi mensuel | trimestriel | semestriel | annuel (exclusif par ligne) */
    const [periodeParClient, setPeriodeParClient] = useState({});
    const [montantsParClient, setMontantsParClient] = useState({});
    const [honoModal, setHonoModal] = useState(null);
    const [montantModalInput, setMontantModalInput] = useState('');
    const [startDateModalInput, setStartDateModalInput] = useState('');
    const [moisModalInput, setMoisModalInput] = useState('1');
    const [trimestreModalInput, setTrimestreModalInput] = useState('1');
    const [semestreModalInput, setSemestreModalInput] = useState('1');
    const [anneeModalInput, setAnneeModalInput] = useState(() => String(getActiveCaisseYear()));
    const [honoModalSaving, setHonoModalSaving] = useState(false);
    const [selectedPeriodeModal, setSelectedPeriodeModal] = useState('');
    const [honoModalError, setHonoModalError] = useState('');
    const [startDatesParClient, setStartDatesParClient] = useState({});
    const [stoppedParClient, setStoppedParClient] = useState({});
    const [honoArretChecked, setHonoArretChecked] = useState(false);
    const [stopDateModalInput, setStopDateModalInput] = useState('');
    const [showContent, setShowContent] = useState(true);
    const [nhTypeParClient, setNhTypeParClient] = useState({});
    const [personneTypeParClient, setPersonneTypeParClient] = useState({});
    const [detailsClient, setDetailsClient] = useState(null);
    /** Modal info personnel (contact + manager) */
    const [personnelModal, setPersonnelModal] = useState(null);
    const [personnelForm, setPersonnelForm] = useState({
        administration_email: '',
        administration_phone_number: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        birth_date: '',
        phone_numbers: '',
        emails: '',
        identity_type: '',
        identity_number: '',
        identity_issue_date: '',
        identity_expiray_date: '',
    });
    const [personnelLoading, setPersonnelLoading] = useState(false);
    const [personnelSaving, setPersonnelSaving] = useState(false);
    const [personnelError, setPersonnelError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showArchives, setShowArchives] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const [transferClient, setTransferClient] = useState(null);
    const [transferCabinets, setTransferCabinets] = useState([]);
    const [selectedTargetCabinetId, setSelectedTargetCabinetId] = useState(null);
    const [transferLoading, setTransferLoading] = useState(false);
    const [transferBusy, setTransferBusy] = useState(false);
    const [transferError, setTransferError] = useState('');
    const navigate = useNavigate();

    // Dans AllClientsPage.js

    const fetchAllClients = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const archivedQs = showArchives ? '?archived=1' : '?archived=0';
            const [resClients, resHono] = await Promise.all([
                fetch(`/api/clients${archivedQs}`, {
                    cache: 'no-store',
                    headers: authJsonHeaders(),
                }),
                fetch('/api/client-hono-periode', {
                    cache: 'no-store',
                    headers: authJsonHeaders(),
                }),
            ]);

            const data = await resClients.json();
            if (data && data.clients) {
                setAllClients(data.clients);

                              const nhTypes = {};
                const personneTypes = {};
                data.clients.forEach(client => {
                    // Si nh_type est null ou undefined, on met 'E' par défaut
                    nhTypes[client.id] = client.nh_type || 'E';
                    personneTypes[client.id] = client.personne_type || '';
                });
                setNhTypeParClient(nhTypes);
                setPersonneTypeParClient(personneTypes);
            } else {
                setAllClients([]);
            }

            // ... reste du code pour resHono
            if (resHono.ok) {
                try {
                    const honoData = await resHono.json();
                    if (honoData.success && Array.isArray(honoData.rows)) {
                        setMontantsParClient(mergeMontantsFromRows(honoData.rows));
                        setStartDatesParClient(mergeStartDatesFromRows(honoData.rows));
                        setStoppedParClient(mergeStoppedFromRows(honoData.rows));
                    }
                } catch (_) { /* ignore */ }
            }

        } catch (err) {
            setError('Erreur lors du chargement des clients');
            setAllClients([]);
        } finally {
            setLoading(false);
        }
    }, [showArchives, activeCabinet?.id]);

    useEffect(() => {
        fetchAllClients();
    }, [fetchAllClients]);

    useEffect(() => {
        setCurrentPage(1);
    }, [showArchives]);

    useEffect(() => {
        return subscribeCaisseYear((y) => {
            setActiveCaisseYearState(y);
            setPeriodeParClient({});
            setAnneeModalInput(String(y));
        });
    }, []);

    const handleCaisseYearChange = (e) => {
        const y = setActiveCaisseYear(parseInt(e.target.value, 10));
        setActiveCaisseYearState(y);
        setPeriodeParClient({});
        setAnneeModalInput(String(y));
    };

    /** Hono visible pour l'exercice sélectionné uniquement (start_date dans cette année). */
    const honoBelongsToActiveYear = (ymd) => {
        const s = toCalendarYmd(ymd);
        if (!s || s.length < 4) return false;
        return parseInt(s.slice(0, 4), 10) === Number(activeCaisseYear);
    };

    const clientMatchesPeriodeFilter = (client, periode) => {
        if (!periode) return true;
        if (periodeParClient[client.id] === periode) return true;
        const m = montantsParClient[client.id];
        const dates = startDatesParClient[client.id] || {};
        if (!m) return false;
        if (!honoBelongsToActiveYear(dates[periode])) return false;
        const v = m[periode];
        return v != null && !Number.isNaN(v);
    };

    const hasActiveClientFilters =
        Boolean(filterNhType) || Boolean(filterPeriode) || Boolean(filterPersonneType);

    // Filtrer les clients (recherche + NH/E, période hono, P.P/P.M)
    const filteredClients = allClients.filter((client) => {
        const search = searchTerm.toLowerCase().trim();
        if (search) {
            const matchesSearch =
                String(client.username || '').toLowerCase().includes(search) ||
                String(client.nom || '').toLowerCase().includes(search) ||
                String(getClientNomComplet(client) || '').toLowerCase().includes(search) ||
                String(client.num_chrono ?? '').includes(search) ||
                String(client.adresse || '').toLowerCase().includes(search) ||
                String(client.telephone || '').includes(search) ||
                String(client.id || '').includes(search);
            if (!matchesSearch) return false;
        }
        if (filterNhType) {
            const nh = nhTypeParClient[client.id] || client.nh_type || 'E';
            if (nh !== filterNhType) return false;
        }
               if (filterPersonneType) {
            const pt = personneTypeParClient[client.id] || client.personne_type || '';
            if (pt !== filterPersonneType) return false;
        }
        if (filterPeriode && !clientMatchesPeriodeFilter(client, filterPeriode)) {
            return false;
        }
        return true;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterNhType, filterPeriode, filterPersonneType, itemsPerPage]);

    const totalClientsFiltered = filteredClients.length;
    const totalPages = Math.max(1, Math.ceil(totalClientsFiltered / itemsPerPage));

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPageClients = useMemo(
        () => filteredClients.slice(indexOfFirstItem, indexOfLastItem),
        [filteredClients, indexOfFirstItem, indexOfLastItem]
    );

    const goToNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const goToPrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const totalClientsCount = allClients.length;
    const visibleClientsCount = filteredClients.length;
    const hasListFilter =
        Boolean(searchTerm.trim()) || hasActiveClientFilters;

    const renderClientsCountBadge = () => (
        <div className="clients-count-badge" aria-live="polite">
            {hasListFilter ? (
                <>
                    <span>
                        <strong>{visibleClientsCount}</strong>
                        {' '}
                        / {totalClientsCount} client{totalClientsCount !== 1 ? 's' : ''} affiché{visibleClientsCount !== 1 ? 's' : ''}
                    </span>
                    <span className="clients-table-count-filtered">(filtres actifs)</span>
                </>
            ) : (
                <span>
                    <strong>{totalClientsCount}</strong>
                    {' '}
                    client{totalClientsCount !== 1 ? 's' : ''}
                </span>
            )}
        </div>
    );

    // Fonction pour mettre à jour le nh_type (mise à jour optimiste)
    /** NH/E verrouillé pour caissier seulement après un choix explicite (nh_type_configured). */
    const isClientNhTypeConfigured = (client) => {
        if (!client) return false;
        const v = client.nh_type_configured;
        return v === 1 || v === true || v === '1';
    };

    const isClientNhTypeLocked = (client) =>
        !userIsAdmin && client != null && isClientNhTypeConfigured(client);

    const handleNhTypeChange = async (clientId, newNhType) => {
        const client = allClients.find(c => c.id === clientId);
        if (client && isClientNhTypeLocked(client)) return;

        // Mise à jour OPTIMISTE immédiate pour que l'UI reste réactive
        setNhTypeParClient(prev => ({
            ...prev,
            [clientId]: newNhType
        }));

        try {
            const response = await fetch(`/api/clients/${clientId}/nh-type`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nh_type: newNhType })
            });

            const data = await response.json();

            if (response.ok) {
                setAllClients(prevClients =>
                    prevClients.map(c =>
                        c.id === clientId
                            ? {
                                ...c,
                                nh_type: newNhType,
                                nh_type_configured: data.nh_type_configured ?? 1
                            }
                            : c
                    )
                ); // ✅ Met à jour juste ce client en local
                // L'état reste cohérent et le checkbox reste coché


            } else {
                // En cas d'erreur, restaurer l'ancien état
                setNhTypeParClient(prev => ({
                    ...prev,
                    [clientId]: newNhType === 'NH' ? 'E' : 'NH'
                }));
                setError(data.error || 'Erreur lors de la mise à jour');
                setTimeout(() => setError(''), 3000);
            }
        } catch (err) {
            // En cas d'erreur réseau, restaurer l'ancien état
            setNhTypeParClient(prev => ({
                ...prev,
                [clientId]: newNhType === 'NH' ? 'E' : 'NH'
            }));
            setError('Erreur réseau lors de la mise à jour');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handlePersonneTypeChange = async (clientId, newPersonneType) => {
        setPersonneTypeParClient(prev => ({
            ...prev,
            [clientId]: newPersonneType
        }));

        try {
            const response = await fetch(`/api/clients/${clientId}/personne-type`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ personne_type: newPersonneType })
            });
            const data = await response.json();

            if (response.ok) {
                setAllClients(prevClients =>
                    prevClients.map(c =>
                        c.id === clientId ? { ...c, personne_type: newPersonneType } : c
                    )
                );
            } else {
                setPersonneTypeParClient(prev => ({
                    ...prev,
                    [clientId]: allClients.find(c => c.id === clientId)?.personne_type || ''
                }));
                setError(data.error || 'Erreur lors de la mise à jour du type de personne');
                setTimeout(() => setError(''), 3000);
            }
        } catch (err) {
            setPersonneTypeParClient(prev => ({
                ...prev,
                [clientId]: allClients.find(c => c.id === clientId)?.personne_type || ''
            }));
            setError('Erreur réseau lors de la mise à jour du type de personne');
            setTimeout(() => setError(''), 3000);
        }
    };

    const openDeleteConfirm = (client) => {
        if (!userIsAdmin) return;
        setConfirmModal({
            type: 'delete',
            clientId: client.id,
            clientName: getClientNomComplet(client) || 'ce client',
        });
    };

    const openArchiveConfirm = (client, archive = true) => {
        setConfirmModal({
            type: archive ? 'archive' : 'unarchive',
            clientId: client.id,
            clientName: getClientNomComplet(client) || 'ce client',
        });
    };

    const openTransferModal = async (client) => {
        setTransferClient(client);
        setTransferError('');
        setTransferCabinets([]);
        setSelectedTargetCabinetId(null);
        setTransferLoading(true);
        try {
            const fromId = activeCabinet?.id;
            const qs = fromId
                ? `?from_cabinet_id=${encodeURIComponent(fromId)}`
                : '';
            // Admin du cabinet source → tous les autres cabinets (pas seulement ceux où il est membre)
            const res = await fetch(`/api/cabinets/transfer-targets${qs}`, {
                headers: authJsonHeaders(),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Impossible de charger les cabinets');
            }
            const list = Array.isArray(data.cabinets) ? data.cabinets : [];
            setTransferCabinets(list);
            if (list.length > 0) {
                setSelectedTargetCabinetId(list[0].id);
            }
            if (list.length === 0) {
                setTransferError(
                    'Aucun autre cabinet en base. Créez CGM2 dans « Gérer cabinet », ou vérifiez que vous êtes Admin de ce cabinet (Point d’accès).'
                );
            }
        } catch (err) {
            setTransferError(err.message || 'Erreur chargement cabinets');
        } finally {
            setTransferLoading(false);
        }
    };

    const handleTransferSubmit = async () => {
        const targetCab = transferCabinets.find((c) => Number(c.id) === Number(selectedTargetCabinetId));
        if (!targetCab) {
            setTransferError('Veuillez sélectionner un cabinet de destination.');
            return;
        }
        await handleTransferToCabinet(targetCab);
    };

    const handleTransferToCabinet = async (toCabinet) => {
        if (!transferClient?.id || !toCabinet?.id || transferBusy) return;
        setTransferBusy(true);
        setTransferError('');
        try {
            const res = await fetch('/api/cabinets/transfer-client', {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify({
                    client_id: transferClient.id,
                    from_cabinet_id: activeCabinet?.id || null,
                    to_cabinet_id: toCabinet.id,
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Déplacement impossible');
            }
            const movedName = transferClient.nom || '';
            setTransferClient(null);
            setSuccessMessage(
                `« ${movedName} » déplacé vers ${toCabinet.name} avec tout son dossier (charges, opérations, tickets)`
            );
            setTimeout(() => setSuccessMessage(''), 4500);
            await fetchAllClients();
        } catch (err) {
            setTransferError(err.message || 'Erreur lors du déplacement');
        } finally {
            setTransferBusy(false);
        }
    };

    const closeConfirmModal = () => {
        if (confirmBusy) return;
        setConfirmModal(null);
    };

    const executeConfirmModal = async () => {
        if (!confirmModal) return;
        const { type, clientId, clientName } = confirmModal;
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        setConfirmBusy(true);
        try {
            if (type === 'delete') {
                if (!userIsAdmin) {
                    setError('Accès refusé : seuls les administrateurs peuvent supprimer un client');
                    setConfirmBusy(false);
                    return;
                }
                const response = await fetch(`/api/clients/${clientId}`, {
                    method: 'DELETE',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                });
                let data = {};
                try {
                    data = await response.json();
                } catch (_) {
                    data = {};
                }
                if (response.ok && data.success) {
                    const idNum = Number(clientId);
                    setAllClients((prev) => prev.filter((c) => Number(c.id) !== idNum));
                    if (selectedClient?.id === clientId) setSelectedClient(null);
                    setSuccessMessage('Client supprimé avec succès.');
                    setError('');
                    setTimeout(() => setSuccessMessage(''), 3000);
                    setConfirmModal(null);
                } else {
                    setError(data.message || data.error || 'Suppression impossible');
                    setTimeout(() => setError(''), 4000);
                }
            } else {
                const archive = type === 'archive';
                const response = await fetch(`/api/clients/${clientId}/archive`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({ archived: archive }),
                });
                let data = {};
                try {
                    data = await response.json();
                } catch (_) {
                    data = {};
                }
                if (response.ok && data.success) {
                    const idNum = Number(clientId);
                    setAllClients((prev) => prev.filter((c) => Number(c.id) !== idNum));
                    if (selectedClient?.id === clientId) setSelectedClient(null);
                    setSuccessMessage(
                        archive
                            ? `${clientName} a été archivé.`
                            : `${clientName} a été désarchivé.`
                    );
                    setError('');
                    setTimeout(() => setSuccessMessage(''), 3000);
                    setConfirmModal(null);
                } else {
                    setError(data.message || data.error || 'Opération impossible');
                    setTimeout(() => setError(''), 4000);
                }
            }
        } catch (err) {
            console.error('Erreur confirmation client:', err);
            setError('Erreur réseau — réessayez.');
            setTimeout(() => setError(''), 4000);
        } finally {
            setConfirmBusy(false);
        }
    };

    const confirmModalCopy = (() => {
        if (!confirmModal) return null;
        const name = confirmModal.clientName;
        if (confirmModal.type === 'delete') {
            return {
                title: 'Supprimer le client',
                icon: 'fa-trash-alt',
                tone: 'danger',
                message: `Voulez-vous vraiment supprimer « ${name} » ?`,
                detail: 'Cette action est définitive. Les données liées pourront être impactées.',
                confirmLabel: 'Supprimer',
            };
        }
        if (confirmModal.type === 'unarchive') {
            return {
                title: 'Désarchiver le client',
                icon: 'fa-undo',
                tone: 'restore',
                message: `Désarchiver « ${name} » ?`,
                detail: 'Le client réapparaîtra dans la liste active des clients.',
                confirmLabel: 'Désarchiver',
            };
        }
        return {
            title: 'Archiver le client',
            icon: 'fa-archive',
            tone: 'archive',
            message: `Archiver « ${name} » ?`,
            detail: 'Le client disparaîtra de la liste active. Les charges et toutes les données sont conservées.',
            confirmLabel: 'Archiver',
        };
    })();

  const handleEditClient = (client) => {
        setEditingClient({
            ...client,
            personne_type: client.personne_type || personneTypeParClient[client.id] || 'Natural',
            nh_type: client.nh_type || nhTypeParClient[client.id] || 'E',
            nh_type_configured: client.nh_type_configured
        });
        setEditMode(true);
        setShowAddForm(false);
        setDeleteMode(false);
    };

    const toYmd = (dateObj) => {
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const applyStartDateToPeriodeInputs = (periode, startDate) => {
        const currentYear = String(new Date().getFullYear());
        const raw = startDate || toYmd(new Date());
        const ymd = raw.length >= 10 ? raw.slice(0, 10) : raw;
        const parts = ymd.split('-');
        const year = parts[0] && /^\d{4}$/.test(parts[0]) ? parts[0] : currentYear;
        const month = Number(parts[1] || 1);

        setAnneeModalInput(year);
        if (periode === 'mensuel') {
            const m = Number.isFinite(month) && month >= 1 && month <= 12 ? String(month) : '1';
            setMoisModalInput(m);
            setStartDateModalInput(`${year}-${String(m).padStart(2, '0')}-01`);
            return;
        }
        if (periode === 'trimestriel') {
            const quarter = month <= 3 ? '1' : month <= 6 ? '2' : month <= 9 ? '3' : '4';
            setTrimestreModalInput(quarter);
            return;
        }
        if (periode === 'semestriel') {
            setSemestreModalInput(month <= 6 ? '1' : '2');
            return;
        }
        if (periode === 'annuel') {
            return;
        }
        setStartDateModalInput(ymd);
    };

    const getExistingPeriodeForClient = (clientId) => {
        if (periodeParClient[clientId]) return periodeParClient[clientId];
        const montants = montantsParClient[clientId] || {};
        const startDates = startDatesParClient[clientId] || {};
        for (const p of ['mensuel', 'trimestriel', 'semestriel', 'annuel']) {
            if (!honoBelongsToActiveYear(startDates[p])) continue;
            const hasMontant = montants[p] != null && !Number.isNaN(montants[p]);
            const hasStartDate = !!startDates[p];
            if (hasMontant || hasStartDate) return p;
        }
        return '';
    };

    const initHonoModalForm = (client) => {
        setHonoModalError('');
        const stoppedInfo = stoppedParClient[client.id];
        const isArret = stoppedInfo?.stopped;
        setHonoArretChecked(!!isArret);
        setStopDateModalInput(stoppedInfo?.stop_date || new Date().toISOString().slice(0, 10));
        const periodeExistante = getExistingPeriodeForClient(client.id);
        setSelectedPeriodeModal(periodeExistante);
        if (periodeExistante) {
            const existingMontant = montantsParClient[client.id]?.[periodeExistante];
            setMontantModalInput(
                existingMontant != null && !Number.isNaN(existingMontant)
                    ? String(existingMontant).replace('.', ',')
                    : ''
            );
            const existingStartDate = startDatesParClient[client.id]?.[periodeExistante];
            applyStartDateToPeriodeInputs(
                periodeExistante,
                existingStartDate || new Date().toISOString().slice(0, 10)
            );
        } else {
            setMontantModalInput('');
            const y = String(new Date().getFullYear());
            const m = String(new Date().getMonth() + 1);
            setMoisModalInput(m);
            setStartDateModalInput(`${y}-${m.padStart(2, '0')}-01`);
            setTrimestreModalInput('1');
            setSemestreModalInput('1');
            setAnneeModalInput(y);
        }
    };

    const openHonoModal = (e, client) => {
        e.stopPropagation();
        e.preventDefault();
        setError('');
        initHonoModalForm(client);
        setHonoModal({ client });
    };

    const resetHonoModalForm = () => {
        if (!honoModal?.client || honoModalSaving) return;
        if (isHonoModalLockedForCaissier(honoModal.client)) return;
        initHonoModalForm(honoModal.client);
    };

    const closeHonoModal = () => {
        setHonoModal(null);
        setSelectedPeriodeModal('');
        setMontantModalInput('');
        setStartDateModalInput('');
        setMoisModalInput('1');
        setTrimestreModalInput('1');
        setSemestreModalInput('1');
        setAnneeModalInput(String(new Date().getFullYear()));
        setHonoModalSaving(false);
        setHonoModalError('');
        setHonoArretChecked(false);
        setStopDateModalInput('');
    };

    const handlePeriodeModalChange = (periode) => {
        if (honoModal?.client && isHonoModalLockedForCaissier(honoModal.client)) return;
        setSelectedPeriodeModal(periode);
        setHonoModalError('');
        if (!honoModal?.client) return;
        const existing = montantsParClient[honoModal.client.id]?.[periode];
        setMontantModalInput(
            existing != null && !Number.isNaN(existing) ? String(existing).replace('.', ',') : ''
        );
        const existingStartDate = startDatesParClient[honoModal.client.id]?.[periode];
        applyStartDateToPeriodeInputs(
            periode,
            existingStartDate || new Date().toISOString().slice(0, 10)
        );
    };

    const handleHonoModalSubmit = async (e) => {
        e.preventDefault();
        if (!honoModal) return;
        if (isHonoModalLockedForCaissier(honoModal.client)) {
            setHonoModalError('Modification réservée à l\'administrateur : honoraires déjà enregistrés.');
            return;
        }
        const periode = selectedPeriodeModal;
        if (!periode) {
            setHonoModalError('Veuillez choisir un type d\'hono.');
            return;
        }

        if (honoArretChecked) {
            if (!stopDateModalInput) {
                setHonoModalError('Veuillez choisir la date d\'arrêt.');
                return;
            }
            const existingMontant = montantsParClient[honoModal.client.id]?.[periode];
            const m = existingMontant != null ? existingMontant : 0;
            const existingStartDate = startDatesParClient[honoModal.client.id]?.[periode];
            setHonoModalError('');
            setError('');
            setHonoModalSaving(true);
            try {
                const res = await fetch('/api/client-hono-periode', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        client_id: honoModal.client.id,
                        periode_type: periode,
                        montant: m,
                        start_date: existingStartDate || new Date().toISOString().slice(0, 10),
                        stopped: true,
                        stop_date: stopDateModalInput
                    })
                });
                const data = await res.json();
                if (!data.success) {
                    setError(data.error || 'Enregistrement impossible');
                    setHonoModalSaving(false);
                    return;
                }
                const id = honoModal.client.id;
                setStoppedParClient((prev) => ({
                    ...prev,
                    [id]: { stopped: true, stop_date: stopDateModalInput }
                }));
                setPeriodeParClient((prev) => ({ ...prev, [id]: periode }));
                setSuccessMessage('Arrêt d\'hono enregistré.');
                setTimeout(() => setSuccessMessage(''), 2500);
                closeHonoModal();
            } catch (err) {
                setError('Erreur réseau lors de l\'enregistrement');
            } finally {
                setHonoModalSaving(false);
            }
            return;
        }

        const trimmed = String(montantModalInput).trim();
        if (!trimmed) {
            setHonoModalError('Veuillez saisir un montant pour le type sélectionné.');
            return;
        }
        const raw = trimmed.replace(/\s/g, '').replace(',', '.');
        const m = parseFloat(raw);
        if (Number.isNaN(m) || m < 0) {
            setHonoModalError('Veuillez saisir un montant valide (≥ 0).');
            return;
        }
        let computedStartDate = startDateModalInput;
        if (periode === 'mensuel') {
            const mois = Number(moisModalInput || 1);
            const y = String(anneeModalInput || '').trim();
            if (!/^\d{4}$/.test(y) || !Number.isFinite(mois) || mois < 1 || mois > 12) {
                setHonoModalError('Veuillez choisir un mois et une année valides.');
                return;
            }
            computedStartDate = `${y}-${String(mois).padStart(2, '0')}-01`;
        } else if (periode === 'trimestriel') {
            const q = Number(trimestreModalInput || 1);
            const y = String(anneeModalInput || '').trim();
            if (!/^\d{4}$/.test(y) || ![1, 2, 3, 4].includes(q)) {
                setHonoModalError('Veuillez choisir un trimestre et une année valides.');
                return;
            }
            const month = String((q - 1) * 3 + 1).padStart(2, '0');
            computedStartDate = `${y}-${month}-01`;
        } else if (periode === 'semestriel') {
            const s = Number(semestreModalInput || 1);
            const y = String(anneeModalInput || '').trim();
            if (!/^\d{4}$/.test(y) || ![1, 2].includes(s)) {
                setHonoModalError('Veuillez choisir un semestre et une année valides.');
                return;
            }
            const month = s === 1 ? '01' : '07';
            computedStartDate = `${y}-${month}-01`;
        } else if (periode === 'annuel') {
            const y = String(anneeModalInput || '').trim();
            if (!/^\d{4}$/.test(y)) {
                setHonoModalError('Veuillez choisir une année valide.');
                return;
            }
            computedStartDate = `${y}-01-01`;
        }
        if (!computedStartDate) {
            setHonoModalError('Veuillez choisir une date de début.');
            return;
        }
        setHonoModalError('');
        setError('');
        setHonoModalSaving(true);
        try {
            const res = await fetch('/api/client-hono-periode', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: honoModal.client.id,
                    periode_type: periode,
                    montant: m,
                    start_date: computedStartDate,
                    stopped: false,
                    stop_date: null
                })
            });
            const data = await res.json();
            if (!data.success) {
                setError(data.error || 'Enregistrement impossible');
                setHonoModalSaving(false);
                return;
            }
            const id = honoModal.client.id;
            const parsedResp = parseFloat(String(data.montant).replace(',', '.'));
            const montantAffiche = Number.isFinite(parsedResp) ? parsedResp : m;
            setMontantsParClient((prev) => ({
                ...prev,
                [id]: { [periode]: montantAffiche }
            }));
            setStartDatesParClient((prev) => ({
                ...prev,
                [id]: { [periode]: data.start_date || computedStartDate }
            }));
            setStoppedParClient((prev) => ({
                ...prev,
                [id]: { stopped: false, stop_date: null }
            }));
            setPeriodeParClient((prev) => ({ ...prev, [id]: periode }));
            setSuccessMessage('Montant enregistré.');
            setTimeout(() => setSuccessMessage(''), 2500);
            closeHonoModal();
        } catch (err) {
            setError('Erreur réseau lors de l\'enregistrement');
        } finally {
            setHonoModalSaving(false);
        }
    };

    const unlockClientPeriodes = (clientId) => {
        setPeriodeParClient((prev) => {
            const next = { ...prev };
            delete next[clientId];
            return next;
        });
    };

    const rowHasHonoOuVerrou = (client) => {
        if (periodeParClient[client.id]) return true;
        const m = montantsParClient[client.id];
        const dates = startDatesParClient[client.id] || {};
        if (!m) return false;
        return ['mensuel', 'trimestriel', 'semestriel', 'annuel'].some(
            (p) =>
                honoBelongsToActiveYear(dates[p]) &&
                m[p] != null &&
                !Number.isNaN(m[p])
        );
    };

    const isHonoModalLockedForCaissier = () => false;

    const honoModalLocked = false;

    /** Montant affiché dans la colonne centrale : période verrouillée si définie, sinon premier montant renseigné */
    const getMontantHonoCentral = (client, choixPeriode) => {
        const m = montantsParClient[client.id];
        const dates = startDatesParClient[client.id] || {};
        if (!m) return null;
        if (
            choixPeriode &&
            honoBelongsToActiveYear(dates[choixPeriode]) &&
            m[choixPeriode] != null &&
            !Number.isNaN(m[choixPeriode])
        ) {
            return formatMontantAffiche(m[choixPeriode]);
        }
        for (const p of ['mensuel', 'trimestriel', 'semestriel', 'annuel']) {
            if (!honoBelongsToActiveYear(dates[p])) continue;
            const v = m[p];
            if (v != null && !Number.isNaN(v)) return formatMontantAffiche(v);
        }
        return null;
    };

    const getTypeHonoCentral = (client, choixPeriode) => {
        const stoppedInfo = stoppedParClient[client.id];
        const dates = startDatesParClient[client.id] || {};
        const baseLabel = (() => {
            if (
                choixPeriode &&
                PERIODE_LABELS[choixPeriode] &&
                honoBelongsToActiveYear(dates[choixPeriode])
            ) {
                return PERIODE_LABELS[choixPeriode];
            }
            const m = montantsParClient[client.id];
            if (!m) return null;
            for (const p of ['mensuel', 'trimestriel', 'semestriel', 'annuel']) {
                if (!honoBelongsToActiveYear(dates[p])) continue;
                const v = m[p];
                if (v != null && !Number.isNaN(v)) return PERIODE_LABELS[p];
            }
            return null;
        })();
        if (stoppedInfo?.stopped && baseLabel) return `${baseLabel} (Arrêté)`;
        return baseLabel;
    };

    /** start_date de la période hono active (affichage DD/MM/YYYY). */
    const getStartDateHonoCentral = (client, choixPeriode) => {
        const dates = startDatesParClient[client.id];
        if (!dates) return null;
        const toDisplay = (ymd) => {
            const s = toCalendarYmd(ymd);
            if (!s || s.length < 10) return null;
            const [y, m, d] = s.slice(0, 10).split('-');
            if (!y || !m || !d) return null;
            return `${d}/${m}/${y}`;
        };
        if (choixPeriode && dates[choixPeriode] && honoBelongsToActiveYear(dates[choixPeriode])) {
            return toDisplay(dates[choixPeriode]);
        }
        for (const p of ['mensuel', 'trimestriel', 'semestriel', 'annuel']) {
            if (dates[p] && honoBelongsToActiveYear(dates[p])) return toDisplay(dates[p]);
        }
        return null;
    };

    const handlePrintClientsHonoraires = () => {
        if (filteredClients.length === 0) return;
        const escapeHtml = (v) =>
            String(v ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        const rows = filteredClients.map((client) => {
            const choixPeriode = periodeParClient[client.id];
            return {
                id: client.id,
                nom: getClientNomComplet(client) || client.nom || '—',
                num_chrono:
                    client.num_chrono != null && client.num_chrono !== ''
                        ? client.num_chrono
                        : '—',
                montant: getMontantHonoCentral(client, choixPeriode) || '—',
                type: getTypeHonoCentral(client, choixPeriode) || '—',
                adresse: client.adresse || '—',
                email: client.email || '—',
                telephone: client.telephone || '—',
                matricule_fiscal: client.matricule_fiscal || '—',
            };
        });
        const tableRows = rows
            .map(
                (r) =>
                    `<tr><td>${escapeHtml(r.num_chrono)}</td><td>${escapeHtml(r.nom)}</td><td class="num">${escapeHtml(r.montant)}</td><td>${escapeHtml(r.type)}</td><td>${escapeHtml(r.adresse)}</td><td>${escapeHtml(r.email)}</td><td>${escapeHtml(r.telephone)}</td><td>${escapeHtml(r.matricule_fiscal)}</td></tr>`
            )
            .join('');
        const printDate = new Date().toLocaleString('fr-FR');
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Liste clients — honoraires</title>
<style>
  body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
  h1 { font-size: 16px; margin: 0 0 8px; }
  .meta { font-size: 11px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; table-layout: fixed; }
  th, td { border: 1px solid #333; padding: 4px 5px; text-align: left; word-wrap: break-word; overflow-wrap: break-word; }
  th { background: #e8eef5; font-weight: 700; }
  td.num { text-align: right; white-space: nowrap; }
  @media print {
    @page { size: landscape; margin: 10mm; }
    body { margin: 0; }
  }
</style></head><body>
<h1>Liste des clients détaillés</h1>
<p class="meta">${rows.length} client(s) — ${escapeHtml(printDate)}</p>
<table>
<thead><tr><th>N° chrono</th><th>Nom</th><th>Montant honoraire</th><th>Type honoraire</th><th>Adresse</th><th>Email</th><th>Téléphone</th><th>Matricule fiscal</th></tr></thead>
<tbody>${tableRows}</tbody>
</table>
</body></html>`;
        // Impression "direct" sans popup (évite les alertes de blocage pop-up).
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.overflow = 'hidden';
        iframe.setAttribute('title', 'print-honoraires');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.srcdoc = html;
        document.body.appendChild(iframe);

        const cleanup = () => {
            try {
                document.body.removeChild(iframe);
            } catch {
                // ignore
            }
        };

        iframe.onload = () => {
            try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            } finally {
                // L'impression peut prendre un peu de temps selon le navigateur.
                setTimeout(cleanup, 500);
            }
        };
    };

    const handleSupprimerHonoLigne = async (e, client) => {
        e.stopPropagation();
        e.preventDefault();
        if (!userIsAdmin) return;
        if (!rowHasHonoOuVerrou(client)) return;
        if (!window.confirm('Êtes-vous sûr de supprimer ça ?')) return;
        const id = client.id;
        try {
            const res = await fetch(`/api/client-hono-periode/client/${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (!data.success) {
                setError(data.error || 'Suppression impossible');
                return;
            }
            setError('');
            setMontantsParClient((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            setStartDatesParClient((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            setStoppedParClient((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            unlockClientPeriodes(id);
            if (honoModal?.client?.id === id) {
                setHonoModal(null);
                setMontantModalInput('');
                setHonoModalSaving(false);
            }
            setSuccessMessage('Honoraires période réinitialisés pour ce client.');
            setTimeout(() => setSuccessMessage(''), 2500);
        } catch (err) {
            setError('Erreur réseau lors de la suppression');
        }
    };

    const openPersonnelModal = async (e, client) => {
        e.stopPropagation();
        setPersonnelError('');
        setPersonnelModal({ client });
        setPersonnelLoading(true);
        setPersonnelForm({
            administration_email: '',
            administration_phone_number: '',
            first_name: '',
            middle_name: '',
            last_name: '',
            birth_date: '',
            phone_numbers: '',
            emails: '',
            identity_type: '',
            identity_number: '',
            identity_issue_date: '',
            identity_expiray_date: '',
        });
        try {
            const res = await fetch(`/api/clients/${client.id}/personnel`, { cache: 'no-store' });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setPersonnelError(data.error || 'Impossible de charger les infos');
                return;
            }
            const asText = (v) => {
                if (v == null) return '';
                if (typeof v === 'object') {
                    try { return Array.isArray(v) ? v.join(', ') : JSON.stringify(v); } catch (_) { return ''; }
                }
                return String(v);
            };
            setPersonnelForm({
                administration_email: asText(data.contact?.administration_email),
                administration_phone_number: asText(data.contact?.administration_phone_number),
                first_name: asText(data.manager?.first_name),
                middle_name: asText(data.manager?.middle_name),
                last_name: asText(data.manager?.last_name),
                birth_date: asText(data.manager?.birth_date).slice(0, 10),
                phone_numbers: asText(data.manager?.phone_numbers),
                emails: asText(data.manager?.emails),
                identity_type: asText(data.identity?.identity_type),
                identity_number: asText(data.identity?.identity_number),
                identity_issue_date: asText(data.identity?.identity_issue_date).slice(0, 10),
                identity_expiray_date: asText(data.identity?.identity_expiray_date).slice(0, 10),
            });
        } catch (err) {
            setPersonnelError('Erreur réseau');
        } finally {
            setPersonnelLoading(false);
        }
    };

    const closePersonnelModal = () => {
        if (personnelSaving) return;
        setPersonnelModal(null);
        setPersonnelError('');
    };

    const handlePersonnelFormChange = (field, value) => {
        setPersonnelForm((prev) => ({ ...prev, [field]: value }));
        if (personnelError) setPersonnelError('');
    };

    const handlePersonnelSubmit = async (e) => {
        e.preventDefault();
        if (!personnelModal?.client || personnelSaving) return;
        setPersonnelSaving(true);
        setPersonnelError('');
        try {
            const res = await fetch(`/api/clients/${personnelModal.client.id}/personnel`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contact: {
                        administration_email: personnelForm.administration_email,
                        administration_phone_number: personnelForm.administration_phone_number,
                    },
                    manager: {
                        first_name: personnelForm.first_name,
                        middle_name: personnelForm.middle_name,
                        last_name: personnelForm.last_name,
                        birth_date: personnelForm.birth_date,
                        phone_numbers: personnelForm.phone_numbers,
                        emails: personnelForm.emails,
                    },
                    identity: {
                        identity_type: personnelForm.identity_type,
                        identity_number: personnelForm.identity_number,
                        identity_issue_date: personnelForm.identity_issue_date,
                        identity_expiray_date: personnelForm.identity_expiray_date,
                    },
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setPersonnelError(data.error || 'Enregistrement impossible');
                return;
            }
            setPersonnelForm({
                administration_email: data.contact?.administration_email || personnelForm.administration_email,
                administration_phone_number: data.contact?.administration_phone_number || personnelForm.administration_phone_number,
                first_name: data.manager?.first_name || personnelForm.first_name,
                middle_name: data.manager?.middle_name || personnelForm.middle_name,
                last_name: data.manager?.last_name || personnelForm.last_name,
                birth_date: data.manager?.birth_date || personnelForm.birth_date,
                phone_numbers: data.manager?.phone_numbers || personnelForm.phone_numbers,
                emails: data.manager?.emails || personnelForm.emails,
                identity_type: data.identity?.identity_type || personnelForm.identity_type,
                identity_number: data.identity?.identity_number || personnelForm.identity_number,
                identity_issue_date: data.identity?.identity_issue_date || personnelForm.identity_issue_date,
                identity_expiray_date: data.identity?.identity_expiray_date || personnelForm.identity_expiray_date,
            });
            setPersonnelModal(null);
            setSuccessMessage('Info personnel enregistrée.');
            setTimeout(() => setSuccessMessage(''), 2500);
            fetchAllClients();
        } catch (err) {
            setPersonnelError('Erreur réseau lors de l\'enregistrement');
        } finally {
            setPersonnelSaving(false);
        }
    };

    return (
        <>
            <style jsx global>{`
                body, html {
                    height: auto !important;
                    overflow-x: hidden;
                    overflow-y: auto;
                    background: #f0f2f5;
                }
                
                .all-clients-page {
                    background: #f0f2f5;
                    min-height: 100vh;
                    padding: 0;
                    margin: 0;
                }

                .dashboard-buttons-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 600px;
                    padding: 2rem;
                    gap: 2rem;
                }

                .dashboard-buttons-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 2rem;
                    max-width: 700px;
                    width: 100%;
                }

                .dashboard-button-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 3rem 2rem;
                    border: none;
                    border-radius: 15px;
                    font-size: 1.3rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    color: white;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    min-height: 250px;
                    white-space: normal;
                    overflow: visible;
                    text-overflow: clip;
                }

                .dashboard-button-item:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
                }

                .dashboard-button-item:active {
                    transform: translateY(-4px);
                }

                .cgm-button {
                    background: linear-gradient(135deg, #667eea 0%, #5568d3 100%);
                }

                .cgm-button:hover {
                    background: linear-gradient(135deg, #7b8ee8 0%, #6878e3 100%);
                }

                .nh-button {
                    background: linear-gradient(135deg, #8eff97 0%, #42eb2c 100%);
                }

                .nh-button:hover {
                    background: linear-gradient(135deg, #b9ffa7 0%, #80f78a 100%);
                }

                .button-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    display: block;
                }

                .button-text {
                    font-size: 1.2rem;
                    line-height: 1.4;
                    text-align: center;
                    max-width: 100%;
                    white-space: normal;
                    overflow: visible;
                    text-overflow: clip;
                    overflow-wrap: break-word;
                    word-wrap: break-word;
                }

                .nh-button .button-text {
                    font-size: 1.05rem;
                    line-height: 1.35;
                    letter-spacing: 0.02em;
                    text-transform: none;
                    padding: 0 0.25rem;
                }

                @media (max-width: 768px) {
                    .dashboard-buttons-grid {
                        grid-template-columns: 1fr;
                        gap: 1.5rem;
                        max-width: 100%;
                    }

                    .dashboard-button-item {
                        min-height: 200px;
                        padding: 2rem 1.5rem;
                        font-size: 1.1rem;
                    }

                    .button-icon {
                        font-size: 3rem;
                        margin-bottom: 0.5rem;
                    }
                }

                .page-shell {
                    max-width: 100%;
                    margin: 0;
                    padding: 0 1.25rem;
                }
                
                .all-clients-header { display: none; } /* supprimer le gros header */

                /* —— Front de caisse (test) : contenu page clients uniquement —— */
                .all-clients-page .clients-pos-view {
                    max-width: none;
                    width: 100%;
                    margin: 0;
                    padding: 0.45rem 1rem 1rem;
                    font-size: 0.875rem;
                    color: #1e293b;
                }

                .all-clients-page .clients-pos-top {
                    margin-bottom: 0.55rem;
                }

                .all-clients-page .clients-pos-crumb {
                    margin: 0 0 0.45rem;
                    font-size: 0.78rem;
                    color: #64748b;
                }

                .all-clients-page .clients-page-topbar {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 0.75rem;
                    width: 100%;
                    margin-bottom: 0.35rem;
                }

                .all-clients-page .clients-table-toolbar {
                    gap: 12px 16px;
                }

                .all-clients-page .clients-table-toolbar__left {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 10px 14px;
                    min-width: 0;
                }

                .all-clients-page .clients-table-count {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.88rem;
                    color: #475569;
                    font-weight: 500;
                }

                .all-clients-page .clients-table-count__label strong {
                    color: #0f766e;
                    font-weight: 700;
                }

                .all-clients-page .clients-table-toolbar__actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    flex-wrap: wrap;
                    gap: 0.55rem;
                    margin-left: auto;
                }

                .all-clients-page .clients-page-topbar .cgm-toolbar-btn--back {
                    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
                    color: #ffffff;
                    border: 1px solid #c82333;
                    min-height: 38px;
                    padding: 7px 14px;
                    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.28);
                }

                .all-clients-page .clients-page-topbar .cgm-toolbar-btn--back:hover:not(:disabled) {
                    background: linear-gradient(135deg, #c82333 0%, #b91c1c 100%);
                    border-color: #b91c1c;
                    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.36);
                }

                .all-clients-page .clients-page-topbar .cgm-toolbar-btn--back .go-back-icon-inline {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 22px;
                    height: 22px;
                    border-radius: 6px;
                    background: rgba(255, 255, 255, 0.2);
                    color: #ffffff;
                    flex-shrink: 0;
                }

                .all-clients-page .clients-page-topbar .cgm-toolbar-btn--back .go-back-icon-inline svg {
                    width: 14px;
                    height: 14px;
                }

                .all-clients-page .clients-page-topbar .cgm-toolbar-btn--back .go-back-icon-inline svg path {
                    fill: currentColor;
                }

                .all-clients-page .clients-page-title-row {
                    justify-content: center;
                    margin-bottom: 0.15rem;
                }

                .all-clients-page .clients-page-title-row .cgm-page-title-wrap {
                    justify-content: center;
                }

                @media (max-width: 768px) {
                    .all-clients-page .clients-page-topbar {
                        gap: 0.5rem;
                    }

                    .all-clients-page .clients-table-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .all-clients-page .clients-table-toolbar__actions {
                        margin-left: 0;
                        justify-content: flex-start;
                    }

                    .all-clients-page .clients-table-toolbar__actions .cgm-toolbar-btn {
                        flex: 1 1 auto;
                        justify-content: center;
                    }
                }

                .all-clients-page .clients-pos-head {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 1rem;
                    margin-bottom: 0.3rem;
                    max-width: 100%;
                    width: 100%;
                    padding-right: 0.35rem;
                }

                .all-clients-page .clients-pos-title {
                    margin: 0;
                    font-size: 1.35rem;
                    font-weight: 700;
                    color: #0f172a;
                }

                .all-clients-page .clients-pos-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    padding: 0.45rem 0.85rem;
                    border: 1px solid #16a34a;
                    border-radius: 6px;
                    background: #16a34a;
                    color: #fff;
                    font-size: 0.82rem;
                    font-weight: 600;
                    cursor: pointer;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .all-clients-page .clients-pos-btn-primary:hover {
                    background: #15803d;
                    border-color: #15803d;
                }

                .all-clients-page .clients-pos-btn-outline {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    padding: 0.4rem 0.75rem;
                    border: 1px solid #cbd5e1;
                    border-radius: 6px;
                    background: #fff;
                    color: #334155;
                    font-size: 0.8rem;
                    font-weight: 600;
                    cursor: pointer;
                }

                .all-clients-page .clients-pos-btn-outline:hover {
                    background: #f8fafc;
                }

                .all-clients-page .clients-pos-btn-outline:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .all-clients-page .clients-pos-centered {
                    width: 100%;
                    max-width: 100%;
                    margin-left: 0;
                    margin-right: 0;
                    padding: 0 0.25rem;
                }

                .all-clients-page .clients-pos-toolbar {
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    width: 100%;
                    max-width: 820px;
                    margin-left: auto;
                    margin-right: auto;
                    margin-bottom: 0.45rem;
                }

                .all-clients-page .clients-pos-actions-bar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 0.55rem;
                    margin-top: 0.4rem;
                    padding-top: 0.4rem;
                    border-top: 1px solid #bfdbfe;
                }

                .all-clients-page .clients-pos-btn-print {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    padding: 0.45rem 0.85rem;
                    border: 1px solid #ca8a04;
                    border-radius: 6px;
                    background: #eab308;
                    color: #422006;
                    font-size: 0.82rem;
                    font-weight: 600;
                    cursor: pointer;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .all-clients-page .clients-pos-btn-print:hover:not(:disabled) {
                    background: #facc15;
                    border-color: #a16207;
                }

                .all-clients-page .clients-pos-btn-print:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .all-clients-page .clients-pos-search-panel {
                    display: block;
                    width: 100%;
                    max-width: 100%;
                    margin-top: 0;
                    margin-bottom: 0;
                    border: 1px solid #93c5fd;
                    border-radius: 6px;
                    background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
                    padding: 0.45rem 0.55rem 0.5rem;
                    box-shadow: 0 1px 4px rgba(37, 99, 235, 0.1);
                }

                .all-clients-page .clients-pos-search-panel h3 {
                    margin: 0 0 0.35rem;
                    padding-bottom: 0.3rem;
                    border-bottom: 1px solid #bfdbfe;
                    font-size: 0.72rem;
                    font-weight: 700;
                    color: #1d4ed8;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }

                .all-clients-page .clients-pos-filters-compact {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: flex-end;
                    justify-content: flex-start;
                    gap: 0.4rem 0.5rem;
                    width: 100%;
                }

                .all-clients-page .clients-pos-field-search {
                    flex: 0 0 auto;
                    width: 165px;
                    max-width: 165px;
                }

                .all-clients-page .clients-pos-filters-group {
                    display: flex;
                    flex-wrap: nowrap;
                    align-items: flex-end;
                    gap: 0.4rem 0.5rem;
                    margin-left: auto;
                }

                .all-clients-page .clients-pos-field {
                    display: flex;
                    flex-direction: column;
                    gap: 0.15rem;
                    min-width: 0;
                    flex: 0 0 auto;
                }

                .all-clients-page .clients-pos-field--filter {
                    width: 100px;
                }

                .all-clients-page .clients-pos-field label {
                    font-size: 0.65rem;
                    font-weight: 700;
                    color: #1e40af;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                    line-height: 1.1;
                }

                .all-clients-page .clients-pos-input,
                .all-clients-page .clients-pos-select {
                    height: 32px;
                    padding: 0 0.4rem;
                    border: 1px solid #93c5fd;
                    border-radius: 4px;
                    background: #fff;
                    font-size: 0.75rem;
                    color: #0f172a;
                    min-width: 0;
                    width: 100%;
                }

                .all-clients-page .clients-pos-input:focus,
                .all-clients-page .clients-pos-select:focus {
                    outline: none;
                    border-color: #2563eb;
                    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
                }

                .all-clients-page .clients-pos-btn-search {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                    flex-shrink: 0;
                    height: 32px;
                    min-width: auto;
                    width: auto;
                    padding: 0 0.85rem;
                    border: 1px solid #1e40af;
                    border-radius: 6px;
                    background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
                    color: #fff;
                    font-size: 0.8rem;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.4);
                    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-btn-search:hover {
                    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
                    box-shadow: 0 3px 8px rgba(29, 78, 216, 0.45);
                }

                .all-clients-page .clients-pos-btn-search:active {
                    transform: scale(0.97);
                }

                .all-clients-page .clients-pos-btn-search:focus-visible {
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.45);
                }

                .all-clients-page .clients-pos-btn-search i {
                    font-size: 0.85rem;
                    line-height: 1;
                }

                .all-clients-page .clients-pos-btn-reset {
                    height: 32px;
                    padding: 0 0.45rem;
                    border: 1px solid #fecaca;
                    border-radius: 4px;
                    background: #fff;
                    color: #dc2626;
                    font-size: 0.68rem;
                    font-weight: 700;
                    cursor: pointer;
                    white-space: nowrap;
                }

                @media (max-width: 768px) {
                    .all-clients-page .clients-pos-centered {
                        max-width: 100%;
                        padding: 0 0.15rem;
                    }

                    .all-clients-page .clients-pos-head {
                        padding-right: 0.15rem;
                    }

                    .all-clients-page .clients-pos-title {
                        font-size: 1.1rem;
                    }

                    .all-clients-page .clients-pos-head .go-back-button {
                        width: 108px;
                        height: 36px;
                        font-size: 0.78rem;
                    }

                    /* Bloc recherche plus petit et centré */
                    .all-clients-page .clients-pos-toolbar {
                        max-width: min(100%, 320px);
                        margin-left: auto;
                        margin-right: auto;
                    }

                    .all-clients-page .clients-pos-search-panel {
                        width: 100%;
                        padding: 0.32rem 0.38rem 0.38rem;
                        border-radius: 5px;
                    }

                    .all-clients-page .clients-pos-search-panel h3 {
                        font-size: 0.62rem;
                        margin-bottom: 0.25rem;
                        padding-bottom: 0.2rem;
                    }

                    .all-clients-page .clients-pos-filters-compact {
                        flex-wrap: wrap;
                        width: 100%;
                        gap: 0.3rem;
                    }

                    .all-clients-page .clients-pos-filters-group {
                        flex-wrap: wrap;
                        width: 100%;
                        margin-left: 0;
                        justify-content: stretch;
                        gap: 0.3rem;
                    }

                    .all-clients-page .clients-pos-field-search {
                        width: 100%;
                        max-width: 100%;
                    }

                    .all-clients-page .clients-pos-field--filter {
                        width: calc(33.333% - 0.25rem);
                        min-width: 0;
                    }

                    .all-clients-page .clients-pos-field label {
                        font-size: 0.58rem;
                    }

                    .all-clients-page .clients-pos-input,
                    .all-clients-page .clients-pos-select {
                        height: 26px;
                        font-size: 0.68rem;
                    }

                    .all-clients-page .clients-pos-btn-search {
                        height: 26px;
                        font-size: 0.68rem;
                        padding: 0 0.5rem;
                        min-width: auto;
                    }

                    .all-clients-page .clients-pos-btn-primary,
                    .all-clients-page .clients-pos-btn-print {
                        font-size: 0.7rem;
                        padding: 0.35rem 0.5rem;
                    }

                    .all-clients-page .clients-pos-actions-bar {
                        gap: 0.35rem;
                        padding-top: 0.3rem;
                    }

                    /* Tableau compact — toutes les colonnes sur l'écran */
                    .all-clients-page .clients-pos-view .table-responsive {
                        overflow-x: visible;
                        margin: 0;
                        border-radius: 6px;
                    }

                    .all-clients-page .clients-pos-view .modern-table {
                        min-width: 0 !important;
                        width: 100% !important;
                        table-layout: fixed;
                        font-size: 0.52rem;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th,
                    .all-clients-page .clients-pos-view .modern-table tbody td {
                        padding: 0.18rem 0.06rem !important;
                        font-size: 0.5rem !important;
                        line-height: 1.1;
                        min-width: 0 !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-username {
                        width: 8% !important;
                        min-width: 0 !important;
                        font-size: 0.48rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-nom {
                        width: 17% !important;
                        min-width: 0 !important;
                        max-width: none !important;
                        font-size: 0.48rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-hono-action {
                        width: 8% !important;
                        min-width: 0 !important;
                        padding: 0.08rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-hono-montant {
                        width: 11% !important;
                        min-width: 0 !important;
                        font-size: 0.46rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-hono-type {
                        width: 12% !important;
                        min-width: 0 !important;
                        font-size: 0.46rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-facturation {
                        width: 9% !important;
                        min-width: 0 !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-dernier-fact {
                        width: 10% !important;
                        min-width: 0 !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-hono-delete {
                        width: 11% !important;
                        min-width: 0 !important;
                        padding: 0.06rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-action,
                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-montant,
                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-type,
                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-facturation,
                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-dernier-fact,
                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-delete {
                        font-size: 0 !important;
                        line-height: 0;
                        padding: 0.2rem 0.04rem !important;
                        text-transform: none;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-username {
                        font-size: 0.48rem !important;
                        line-height: 1.1;
                        padding: 0.2rem 0.04rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-nom {
                        font-size: 0.48rem !important;
                        line-height: 1.1;
                        padding: 0.2rem 0.04rem !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-action::after {
                        content: 'Hono.';
                        font-size: 0.46rem;
                        line-height: 1.1;
                        display: block;
                        color: #fff;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-montant::after {
                        content: 'Mnt';
                        font-size: 0.46rem;
                        line-height: 1.1;
                        display: block;
                        color: #fff;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-type::after {
                        content: 'Type';
                        font-size: 0.46rem;
                        line-height: 1.1;
                        display: block;
                        color: #fff;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-facturation::after {
                        content: 'Fact.';
                        font-size: 0.44rem;
                        line-height: 1.1;
                        display: block;
                        color: #fff;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-dernier-fact::after {
                        content: 'Date';
                        font-size: 0.44rem;
                        line-height: 1.1;
                        display: block;
                        color: #fff;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-pos-view .modern-table thead th.col-client-hono-delete::after {
                        content: 'Act.';
                        font-size: 0.46rem;
                        line-height: 1.1;
                        display: block;
                        color: #fff;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-pos-view .modern-table .btn-gerer-hono.uiverse-table-button {
                        min-width: 0 !important;
                        width: 20px !important;
                        height: 20px !important;
                        padding: 0 !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .btn-gerer-hono span {
                        font-size: 0 !important;
                        width: 0;
                        overflow: hidden;
                    }

                    .all-clients-page .clients-pos-view .modern-table .btn-gerer-hono span svg {
                        width: 10px !important;
                        height: 10px !important;
                        margin: 0 !important;
                    }

                    .all-clients-page .clients-pos-view .modern-table .hono-montant-central,
                    .all-clients-page .clients-pos-view .modern-table .hono-type-central {
                        font-size: 0.46rem !important;
                        padding: 0.05rem 0.12rem;
                    }

                    .all-clients-page .clients-pos-view .modern-table .hono-type-central {
                        border-radius: 3px;
                    }

                    .all-clients-page .clients-pos-view .client-row-actions .btn-voir-client,
                    .all-clients-page .clients-pos-view .client-row-actions .btn-modifier-client,
                    .all-clients-page .clients-pos-view .client-row-actions .btn-supprimer-hono {
                        width: 18px;
                        height: 18px;
                        min-width: 18px;
                        font-size: 0.55rem;
                    }

                    .all-clients-page .clients-pos-view .modern-table .col-client-hono-delete .client-row-actions {
                        gap: 2px;
                    }
                }

                .all-clients-page .clients-pos-meta {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    margin-bottom: 0.5rem;
                    font-size: 0.82rem;
                    color: #475569;
                    text-align: center;
                }

                .all-clients-page .clients-pos-centered .table-container {
                    width: 100%;
                    max-width: 100%;
                }

                .all-clients-page .clients-pos-centered .clients-table-zone,
                .all-clients-page .clients-pos-centered .table-responsive {
                    width: 100%;
                    max-width: 100%;
                }

                .all-clients-page .clients-pos-meta strong {
                    color: #1d4ed8;
                }

                .all-clients-page .clients-pos-view .table-container {
                    background: transparent;
                    border: 0;
                    padding: 0;
                    box-shadow: none;
                }

                .all-clients-page .clients-pos-view .clients-table-actions,
                .all-clients-page .clients-pos-view .clients-table-filters-bar {
                    display: none;
                }

                .all-clients-page .clients-pos-view .table-responsive {
                    width: 100%;
                    max-width: 100%;
                    margin: 0;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    box-shadow: none;
                    cursor: default;
                    overflow-x: auto;
                }

                .all-clients-page .clients-pos-view .modern-table {
                    margin: 0;
                    font-size: 0.8rem;
                    table-layout: auto;
                    width: 100%;
                    min-width: 920px;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-username {
                    width: 4%;
                    min-width: 52px;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-nom {
                    width: 22%;
                    min-width: 140px;
                    max-width: none;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-hono-action {
                    width: 9%;
                    min-width: 88px;
                    text-align: center;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-hono-montant {
                    width: 12%;
                    min-width: 118px;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-hono-type {
                    width: 11%;
                    min-width: 108px;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-facturation {
                    width: 10%;
                    min-width: 95px;
                    text-align: center;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-dernier-fact {
                    width: 13%;
                    min-width: 120px;
                    text-align: center;
                    white-space: nowrap;
                }

                .client-facture-badge {
                    display: inline-block;
                    padding: 0.2rem 0.55rem;
                    border-radius: 999px;
                    font-size: 0.72rem;
                    font-weight: 700;
                    letter-spacing: 0.02em;
                }

                .client-facture-badge--oui {
                    background: #dcfce7;
                    color: #166534;
                    border: 1px solid #86efac;
                }

                .client-facture-badge--non {
                    background: #fef3c7;
                    color: #92400e;
                    border: 1px solid #fcd34d;
                }

                .client-dernier-fact-date {
                    font-weight: 600;
                    font-size: 0.8rem;
                    color: #1e3a8a;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-hono-delete {
                    width: 10%;
                    min-width: 96px;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table {
                    border-collapse: collapse;
                    border: 1px solid #000000;
                }

                .all-clients-page .clients-pos-view .modern-table thead th {
                    background: #2563eb;
                    color: #ffffff;
                    font-size: 0.72rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    padding: 0.5rem 0.65rem;
                    border: 1px solid #000000;
                    white-space: nowrap;
                }

                .all-clients-page .clients-pos-view .modern-table thead th,
                .all-clients-page .clients-pos-view .modern-table tbody td {
                    padding: 0.4rem 0.55rem;
                }

                .all-clients-page .clients-pos-view .modern-table tbody td {
                    font-size: 0.76rem;
                    border: 1px solid #000000;
                    vertical-align: middle;
                }

                .all-clients-page .clients-pos-view .modern-table .hono-cell-inner {
                    display: flex;
                    justify-content: center;
                }

                .all-clients-page .clients-pos-view .modern-table .btn-gerer-hono span {
                    font-size: 0.72rem;
                }

                .all-clients-page .clients-pos-view .modern-table .col-client-hono-delete .client-row-actions {
                    gap: 3px;
                }

                .all-clients-page .clients-pos-view .modern-table tbody tr:hover td {
                    background: #f8fafc;
                }

                .all-clients-page .clients-pos-view .client-row-selected td {
                    background: #eff6ff !important;
                }

                .all-clients-page .clients-pos-view .btn-gerer-hono.uiverse-table-button {
                    min-width: 0 !important;
                    width: auto !important;
                    height: auto !important;
                    padding: 0 !important;
                    background: transparent !important;
                    border: 0 !important;
                    box-shadow: none !important;
                    color: #2563eb !important;
                    font-size: 0.8rem !important;
                    font-weight: 600 !important;
                }

                .all-clients-page .clients-pos-view .btn-gerer-hono span {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.2rem;
                }

                .all-clients-page .clients-pos-view .btn-gerer-hono span svg {
                    display: none;
                }

                .all-clients-page .clients-pos-view .hono-type-central {
                    display: inline-block;
                    padding: 0.12rem 0.45rem;
                    border-radius: 4px;
                    background: #f1f5f9;
                    color: #334155;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: capitalize;
                }

                .all-clients-page .clients-pos-view .hono-montant-central {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #1d4ed8;
                }

                /* Tableau clients — même style que NH (purple modern table) */
                .all-clients-page .clients-modern-table-card {
                    margin-top: 0;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table {
                    border: none !important;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-username {
                    display: none !important;
                }

                .modern-table .col-client-username {
                    display: none !important;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-chrono {
                    width: 72px;
                    min-width: 64px;
                    max-width: 88px;
                    text-align: center;
                    font-weight: 600;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-transfer {
                    width: 44px;
                    min-width: 40px;
                    max-width: 48px;
                    text-align: center;
                    padding-left: 0.35rem !important;
                    padding-right: 0.35rem !important;
                }

                .all-clients-page .nh-action-icon-btn--transfer {
                    color: #ffffff;
                    border-color: #16a34a;
                    background: #16a34a;
                }

                .all-clients-page .nh-action-icon-btn--transfer:hover {
                    color: #ffffff;
                    border-color: #15803d;
                    background: #15803d;
                }

                .client-transfer-plaque {
                    width: min(440px, 100%);
                }

                .client-transfer-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    max-height: 320px;
                    overflow: auto;
                    margin-top: 0.75rem;
                }

                .client-transfer-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    width: 100%;
                    text-align: left;
                    padding: 0.75rem 0.95rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    background: #ffffff;
                    color: #1e293b;
                    cursor: pointer;
                    transition: all 0.18s ease;
                    user-select: none;
                }

                .client-transfer-item:hover:not(:disabled) {
                    background: #f0fdfa;
                    border-color: #5eead4;
                }

                .client-transfer-item--selected {
                    border-color: #10b981 !important;
                    background: #ecfdf5 !important;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
                }

                .client-transfer-item:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .client-transfer-item__name {
                    font-weight: 700;
                    font-size: 0.92rem;
                    flex: 1;
                    color: #0f172a;
                }

                .client-transfer-item__meta {
                    font-size: 0.78rem;
                    color: #64748b;
                    font-weight: 500;
                }

                .client-transfer-item i.client-transfer-item__check {
                    color: #10b981;
                    font-size: 1.15rem;
                }

                .client-confirm-btn--validate {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
                    color: #ffffff !important;
                    border: 1px solid #059669 !important;
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                }

                .client-confirm-btn--validate:hover:not(:disabled) {
                    background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
                    box-shadow: 0 4px 14px rgba(16, 185, 129, 0.45);
                    transform: translateY(-1px);
                }

                .client-confirm-btn--validate:disabled {
                    background: #94a3b8 !important;
                    border-color: #94a3b8 !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                    opacity: 0.65;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-nom {
                    width: 140px;
                    min-width: 110px;
                    max-width: 160px;
                    text-align: left;
                    font-weight: 500;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-action {
                    width: 100px;
                    text-align: center;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-montant {
                    width: 120px;
                    text-align: center;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-type {
                    width: 110px;
                    text-align: center;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-start {
                    width: 100px;
                    min-width: 88px;
                    text-align: center;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                    font-size: 0.82rem;
                    color: #334155;
                    font-weight: 600;
                }

                .all-clients-page .clients-hono-start-date {
                    display: inline-block;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-facturation {
                    width: 100px;
                    text-align: center;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-dernier-fact {
                    width: 120px;
                    text-align: center;
                }

                .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-delete {
                    width: 220px;
                    min-width: 200px;
                    text-align: center;
                }

                .all-clients-page .clients-hono-manage-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    border: 1px solid #99f6e4;
                    background: #f0fdfa;
                    color: #0f766e;
                    font-size: 0.76rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.15s ease, border-color 0.15s ease;
                }

                .all-clients-page .clients-hono-manage-btn:hover {
                    background: #ccfbf1;
                    border-color: #0f766e;
                }

                .all-clients-page .clients-hono-manage-btn svg {
                    flex-shrink: 0;
                }

                .all-clients-page .client-facture-badge {
                    display: inline-block;
                    padding: 3px 8px;
                    border-radius: 6px;
                    font-size: 0.78rem;
                    font-weight: 600;
                }

                .all-clients-page .client-facture-badge--oui {
                    background: #ecfdf5;
                    color: #047857;
                }

                .all-clients-page .client-facture-badge--non {
                    background: #fff7ed;
                    color: #c2410c;
                }

                .all-clients-page .clients-pos-view .client-row-actions .btn-voir-client,
                .all-clients-page .clients-pos-view .client-row-actions .btn-modifier-client,
                .all-clients-page .clients-pos-view .client-row-actions .btn-supprimer-hono {
                    width: 24px;
                    height: 24px;
                    min-width: 24px;
                    border-radius: 5px;
                    font-size: 0.68rem;
                }

                .all-clients-page .clients-pos-view .clients-pos-head .go-back-button {
                    position: relative;
                    left: auto;
                    right: auto;
                    top: auto;
                    bottom: auto;
                    flex-shrink: 0;
                    margin-top: 0.15rem;
                }
                
                .container {
                    padding-top: 0;
                    margin-top: 0;
                    width: 100%;
                    max-width: none;
                    padding-left: 0;
                    padding-right: 0;
                }
                
                .all-clients-title {
                    background: linear-gradient(45deg, #667eea, #764ba2);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    font-size: 2.5rem;
                    font-weight: 800;
                    margin-bottom: 1rem;
                    text-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                }
                
                .all-clients-subtitle {
                    color: #6c757d;
                    font-size: 1.1rem;
                    font-weight: 500;
                    margin-bottom: 2rem;
                }
                
                .modern-back-btn {
                    background: linear-gradient(45deg, #6c757d, #495057);
                    border: none;
                    color: white;
                    border-radius: 12px;
                    padding: 10px 20px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .modern-back-btn:hover {
                    background: linear-gradient(45deg, #5a6268, #343a40);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    color: white;
                    text-decoration: none;
                }
                
                .container {
                    position: relative;
                }

                
                .actions-grid {
                    display: flex;
                    flex-direction: row;
                    gap: 0.5rem;
                    margin: 0;
                    flex: 0 0 auto;
                    align-items: center;
                }
                
                .action-btn {
                    border-radius: 4px;
                    padding: 4px 8px;
                    font-weight: 500;
                    font-size: 0.75rem;
                    transition: all 0.12s ease;
                    border: 1px solid transparent;
                    position: relative;
                    overflow: hidden;
                    min-width: 0;
                    box-shadow: none;
                    letter-spacing: 0.01px;
                    cursor: pointer;
                }
                
                .action-btn i {
                    font-size: 0.7rem;
                    margin-right: 4px;
                }
                
                .action-btn::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                    transition: left 0.6s;
                }
                
                .action-btn:hover::before {
                    left: 100%;
                }
                
                .action-btn::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 0;
                    height: 0;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.2);
                    transform: translate(-50%, -50%);
                    transition: width 0.6s, height 0.6s;
                }
                
                .action-btn:active::after {
                    width: 300px;
                    height: 300px;
                }
                
                /* Ajouter / Modifier = vert */
                .action-btn-primary,
                .action-btn-warning,
                .action-btn-success {
                    background: #2E7D32;
                    color: white;
                    border-color: #2E7D32;
                }
                
                .action-btn-primary:hover,
                .action-btn-warning:hover,
                .action-btn-success:hover {
                    background: #256528;
                    border-color: #256528;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 14px rgba(46, 125, 50, 0.2);
                }
                
                /* Supprimer = rouge */
                .action-btn-danger {
                    background: #C62828; /* rouge sérieux */
                    color: white;
                    border-color: #C62828;
                }
                
                .action-btn-danger:hover {
                    background: #a32121;
                    border-color: #a32121;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 14px rgba(198, 40, 40, 0.2);
                }
                
                .action-btn:active {
                    transform: translateY(-2px) scale(0.98);
                }

                .action-btn:disabled {
                    opacity: 0.45;
                    cursor: not-allowed;
                    transform: none !important;
                    box-shadow: none !important;
                    filter: grayscale(0.3);
                }

                .action-btn:disabled:hover {
                    transform: none !important;
                    box-shadow: none !important;
                }

                .action-btn:disabled::before,
                .action-btn:disabled::after {
                    display: none;
                }

                .client-row-selected {
                    background: #e8f0fe !important;
                    outline: 2px solid #3b82f6;
                    outline-offset: -2px;
                }

                .client-row-selected td {
                    color: #1e40af;
                    font-weight: 600;
                }

                .uiverse-action-button {
                    display: inline-block;
                    border-radius: 8px;
                    border: none;
                    text-align: center;
                    font-size: 0.84rem;
                    padding: 0 14px;
                    width: 145px;
                    height: 45px;
                    transition: 0.3s;
                    cursor: pointer;
                    margin: 5px;
                    overflow: hidden;
                    box-shadow: none;
                }

                .uiverse-action-button span {
                    cursor: pointer;
                    display: inline-block;
                    position: static;
                    transition: none;
                }

                .uiverse-action-button.action-btn-primary {
                    background-color: #16a34a;
                    color: #ffffff;
                }

                .uiverse-action-button.action-btn-primary:hover {
                    background-color: #15803d;
                    box-shadow: 0 0 0 6px rgba(22, 163, 74, 0.35), 0 10px 18px rgba(22, 163, 74, 0.28);
                    color: #ffffff;
                    transform: translateY(-1px);
                }

                .uiverse-action-button.action-btn-warning {
                    background-color: #3b82f6;
                    color: #ffffff;
                }

                .uiverse-action-button.action-btn-warning:hover {
                    background-color: #1d4ed8;
                    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.37), 0 10px 18px rgba(59, 130, 246, 0.28);
                    color: #ffffff;
                    transform: translateY(-1px);
                }

                .uiverse-action-button.action-btn-danger {
                    background-color: #dc2626;
                    color: #ffffff;
                }

                .uiverse-action-button.action-btn-danger:hover {
                    background-color: #b91c1c;
                    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0.32), 0 10px 18px rgba(220, 38, 38, 0.26);
                    color: #ffffff;
                    transform: translateY(-1px);
                }

                .uiverse-action-button.action-btn-secondary {
                    background-color: #6b7280;
                    color: #ffffff;
                }

                .uiverse-action-button.action-btn-secondary:hover {
                    background-color: #4b5563;
                    box-shadow: 0 0 0 6px rgba(107, 114, 128, 0.32), 0 10px 18px rgba(75, 85, 99, 0.24);
                    color: #ffffff;
                    transform: translateY(-1px);
                }

                .floating-back-btn {
                    position: fixed;
                    left: 24px;
                    bottom: 24px;
                    z-index: 1000;
                }

                .go-back-button {
                    width: 132px;
                    height: 42px;
                    position: fixed;
                    left: 8px;
                    bottom: 10px;
                    z-index: 1000;
                    border: none;
                    border-radius: 12px;
                    background: #ffffff;
                    color: #000000;
                    font-size: 0.9rem;
                    font-weight: 600;
                    text-align: center;
                    cursor: pointer;
                    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
                    overflow: hidden;
                }

                .go-back-icon {
                    position: absolute;
                    left: 4px;
                    top: 4px;
                    z-index: 10;
                    display: flex;
                    width: 36px;
                    height: 34px;
                    align-items: center;
                    justify-content: center;
                    border-radius: 9px;
                    background: #ef4444;
                    transition: width 0.5s ease;
                }

                .go-back-button:hover .go-back-icon {
                    width: 124px;
                }

                .go-back-text {
                    position: relative;
                    z-index: 1;
                    margin: 0;
                    transform: translateX(8px);
                }

                .uiverse-table-button {
                    display: inline-block;
                    border-radius: 8px;
                    border: none;
                    text-align: center;
                    font-size: 0.7rem;
                    padding: 0 8px;
                    min-width: 78px;
                    height: 32px;
                    transition: 0.3s;
                    cursor: pointer;
                    overflow: hidden;
                }

                .uiverse-table-button span {
                    cursor: pointer;
                    display: inline-block;
                    position: static;
                    transition: none;
                }

                .modern-table .btn-gerer-hono.uiverse-table-button {
                    background: #eab308;
                    color: #ffffff;
                    border: none;
                    font-weight: 500;
                    padding: 0 8px;
                    text-decoration: none;
                }

                .modern-table .btn-gerer-hono.uiverse-table-button:hover {
                    background: #ca8a04;
                    color: #ffffff;
                    box-shadow: 0 0 0 6px rgba(234, 179, 8, 0.35), 0 8px 16px rgba(202, 138, 4, 0.25);
                    text-decoration: none;
                    transform: translateY(-1px);
                }
                
                /* Boutons dans les lignes du tableau - plus visibles */
                .action-btn-inline {
                    padding: 8px 12px !important;
                    font-size: 0.8rem !important;
                    min-width: 80px;
                    font-weight: 600;
                    border-radius: 6px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
                }
                
                .action-btn-inline i {
                    font-size: 0.85rem !important;
                    margin-right: 4px !important;
                }
                
                .action-btn-inline:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
                }
                
                .action-btn-secondary {
                    background: #6c757d;
                    color: white;
                    border-color: #6c757d;
                }
                
                .action-btn-secondary:hover {
                    background: #5a6268;
                    border-color: #5a6268;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 14px rgba(108, 117, 125, 0.2);
                }
                
                .search-container {
                    background: #e5e7eb;
                    border-radius: 10px;
                    padding: 0.25rem;
                    box-shadow: none;
                    border: none;
                    backdrop-filter: none;
                    margin: 0;
                    display: flex;
                    justify-content: center;
                    max-width: 430px;
                    width: 100%;
                }

                .search-box {
                    display: flex;
                    width: 100%;
                    min-height: 34px;
                }

                .search-icon-box {
                    display: flex;
                    width: 38px;
                    align-items: center;
                    justify-content: center;
                    border-right: 1px solid #e5e7eb;
                    border-radius: 8px 0 0 8px;
                    background: #ffffff;
                }

                .search-icon-box svg {
                    width: 15px;
                    fill: #6b7280;
                    transition: fill 0.2s ease;
                }

                .search-input {
                    width: 100%;
                    border: none;
                    border-radius: 0;
                    padding: 0 0.7rem;
                    font-size: 0.78rem;
                    font-weight: 500;
                    transition: all 0.12s ease;
                    background: #ffffff;
                    display: block;
                    outline: 0;
                }
                
                .search-input:focus {
                    outline: none;
                    background: white;
                    box-shadow: none;
                }

                .search-box:focus-within .search-icon-box svg {
                    fill: #2563eb;
                }

                .search-submit-btn {
                    border: none;
                    border-radius: 0 8px 8px 0;
                    background: #3b82f6;
                    color: #ffffff;
                    font-size: 0.76rem;
                    font-weight: 600;
                    padding: 0 0.85rem;
                    min-width: 70px;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }

                .search-submit-btn:hover {
                    background: #1e40af;
                }
                
                .table-container {
                    background: #f0f2f5;
                    border-radius: 0;
                    padding: 0.35rem 0.75rem 0.75rem;
                    box-shadow: none;
                    border: 0;
                    border-top: 1px solid #94a3b8;
                    backdrop-filter: none;
                    width: 100%;
                    max-width: none;
                    margin: 0;
                    min-height: 0;
                    overflow: visible;
                }

                .table-container .table-responsive {
                    border: 2px solid #64748b;
                    border-radius: 12px;
                    display: block;
                    width: min(88vw, 980px);
                    max-width: 100%;
                    margin: 0 auto;
                    max-height: none;
                    min-height: 0;
                    overflow: hidden;
                    overflow-x: auto !important;
                    overflow-y: visible;
                    background: #ffffff;
                    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
                    -webkit-overflow-scrolling: touch;
                    cursor: grab;
                    padding-bottom: 4px;
                    scrollbar-color: #64748b #e5e7eb;
                    scrollbar-width: auto;
                }

                .table-container .table-responsive:active {
                    cursor: grabbing;
                }

                .table-container .table-responsive::-webkit-scrollbar {
                    width: 10px;
                    height: 12px;
                }

                .table-container .table-responsive::-webkit-scrollbar-track {
                    background: #e5e7eb;
                    border-radius: 999px;
                }

                .table-container .table-responsive::-webkit-scrollbar-thumb {
                    background: #94a3b8;
                    border-radius: 999px;
                }

                @media (max-width: 900px) {
                    .table-container .table-responsive {
                        max-height: none;
                    }
                }

                .clients-table-actions {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin: 0 0 0.65rem 0;
                    padding: 0;
                }

                .clients-search-count-wrap {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    flex-wrap: nowrap;
                    max-width: 100%;
                }

                .clients-count-badge {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 0.35rem 0.5rem;
                    padding: 0.4rem 0.75rem;
                    background: #f0f9ff;
                    border: 1px solid #bae6fd;
                    border-radius: 10px;
                    font-size: 0.88rem;
                    color: #1e3a8a;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .clients-count-badge strong {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #1d4ed8;
                }

                .clients-print-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.45rem 0.85rem;
                    border: 1px solid #64748b;
                    border-radius: 10px;
                    background: #fff;
                    color: #334155;
                    font-size: 0.88rem;
                    font-weight: 600;
                    cursor: pointer;
                    flex-shrink: 0;
                    transition: background 0.15s, border-color 0.15s;
                }

                .clients-print-btn:hover {
                    background: #f1f5f9;
                    border-color: #475569;
                }

                .clients-table-zone {
                    width: 100%;
                    min-width: 0;
                }

                .clients-table-filters-bar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 0.75rem 1rem;
                    margin: 0 0 0.65rem 0;
                    padding: 0.5rem 0.85rem;
                    background: #f0f9ff;
                    border: 1px solid #bae6fd;
                    border-radius: 10px;
                    overflow-x: auto;
                }

                .clients-filter-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.4rem 0.9rem;
                    border: 1px solid #2e7d32;
                    border-radius: 8px;
                    background: #2e7d32;
                    color: #fff;
                    font-size: 0.84rem;
                    font-weight: 600;
                    cursor: pointer;
                    white-space: nowrap;
                    flex-shrink: 0;
                    transition: background 0.15s ease, border-color 0.15s ease;
                }

                .clients-filter-add-btn:hover {
                    background: #256528;
                    border-color: #256528;
                }

                .clients-table-filters-inline {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 0.75rem 1rem;
                    flex-shrink: 0;
                }

                .clients-table-count-filtered {
                    font-size: 0.85rem;
                    color: #475569;
                    font-weight: 500;
                }

                .clients-filter-group {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    margin: 0;
                    white-space: nowrap;
                }

                .clients-filter-group label {
                    font-size: 0.78rem;
                    font-weight: 700;
                    color: #1e40af;
                    margin: 0;
                }

                .clients-filter-select {
                    width: auto;
                    min-width: 7.5rem;
                    max-width: 9.5rem;
                    padding: 0.35rem 0.5rem;
                    font-size: 0.82rem;
                    border: 1px solid #93c5fd;
                    border-radius: 8px;
                    background: #fff;
                    color: #0f172a;
                    cursor: pointer;
                    box-shadow: 0 1px 4px rgba(30, 64, 175, 0.1);
                }

                .clients-filter-select:focus {
                    outline: none;
                    border-color: #ffffff;
                    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.55);
                }

                .clients-filters-reset {
                    width: auto;
                    margin: 0;
                    padding: 0.35rem 0.65rem;
                    font-size: 0.78rem;
                    font-weight: 600;
                    border: 1px solid #93c5fd;
                    border-radius: 8px;
                    background: #fff;
                    color: #1d4ed8;
                    cursor: pointer;
                    white-space: nowrap;
                    box-shadow: 0 1px 4px rgba(30, 64, 175, 0.1);
                }

                .clients-filters-reset:hover {
                    background: #eff6ff;
                    color: #1e3a8a;
                }

                .clients-filter-label-short {
                    display: none;
                }

                .clients-filter-label-long {
                    display: inline;
                }

                @media (max-width: 900px) {
                    .clients-search-count-wrap {
                        flex-direction: column;
                        align-items: stretch;
                        width: 100%;
                    }

                    .clients-count-badge {
                        justify-content: center;
                    }

                    .clients-table-filters-bar {
                        flex-wrap: wrap;
                        justify-content: center;
                    }
                }
                
                .table-title {
                    color: #2c3e50;
                    font-weight: 700;
                    font-size: 0.9rem;
                    margin-bottom: 0.15rem;
                    text-align: center;
                }
                
                .modern-table {
                    margin: 0;
                    border-radius: 0;
                    overflow: hidden;
                    box-shadow: none;
                    border-collapse: collapse;
                    border-spacing: 0;
                    width: 100%;
                    min-width: 0;
                    table-layout: fixed;
                    background: #ffffff;
                    font-family: "Inter", "Segoe UI", Arial, sans-serif;
                    border: 1px solid #000000;
                }
                
                 .modern-table thead {
                     background: #e0f2fe;
                 }
                 
                 .modern-table thead th {
                     background-color: #e0f2fe;
                     border: 1px solid #000000;
                     color: #0f172a;
                     font-weight: 500;
                     padding: 0.65rem 0.55rem;
                     text-align: left;
                     vertical-align: middle;
                     font-size: 0.74rem;
                     letter-spacing: 0;
                     font-family: inherit;
                 }
                 
                .modern-table tbody td {
                    border: 1px solid #000000;
                    color: #374151;
                    font-weight: 400;
                    padding: 0.62rem 0.55rem;
                    text-align: left;
                    vertical-align: middle;
                    font-size: 0.8rem;
                    line-height: 1.35;
                    background: #ffffff;
                }

                /* Largeurs de colonnes et alignement */
                .modern-table .col-client-avatar {
                    width: 42px;
                    text-align: center;
                }

                .modern-table .col-client-username {
                    display: none !important;
                    width: 0;
                }

                .modern-table .col-client-chrono {
                    width: 72px;
                    min-width: 64px;
                    max-width: 88px;
                    text-align: center;
                    font-weight: 600;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }

                .modern-table .col-client-nom {
                    width: 140px;
                    min-width: 110px;
                    max-width: 160px;
                    text-align: left;
                    font-weight: 500;
                    color: #1f2937;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .modern-table .col-client-personne {
                    width: 105px;
                    text-align: center;
                    vertical-align: middle;
                }

                .personne-choice-group {
                    display: inline-flex;
                    gap: 0.22rem;
                    align-items: center;
                    justify-content: center;
                }

                .personne-choice {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    margin: 0;
                    color: #334155;
                    font-size: 0.68rem;
                    font-weight: 500;
                    cursor: pointer;
                    user-select: none;
                }

                .personne-choice input {
                    width: 14px;
                    height: 14px;
                    margin: 0;
                    cursor: pointer;
                    accent-color: #2563eb;
                }

                .modern-table .col-client-hono-action {
                    width: 105px;
                    text-align: center;
                    color: #6b7280;
                    font-size: 0.88rem;
                }

                .modern-table thead th.col-client-hono-action {
                    color: #111827;
                    font-size: 0.82rem;
                    font-weight: 500;
                }

                .modern-table tbody td.col-client-hono-action {
                    vertical-align: middle;
                }

                .modern-table .btn-gerer-hono {
                    background: transparent;
                    color: #2563eb;
                    border: 0;
                    border-radius: 6px;
                    padding: 0;
                    font-size: 0.92rem;
                    font-weight: 500;
                    cursor: pointer;
                    white-space: nowrap;
                    line-height: 1.2;
                    transition: color 0.15s ease;
                }

                .modern-table .btn-gerer-hono:hover {
                    color: #1d4ed8;
                    text-decoration: underline;
                }

                .modern-table .btn-gerer-hono:active {
                    transform: scale(0.98);
                }

                .modern-table .col-client-actions {
                    width: 14%;
                    text-align: center;
                }

                .modern-table .col-client-hono-montant {
                    width: 110px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 0.9rem;
                }

                .modern-table thead th.col-client-hono-montant {
                    color: #111827;
                    font-size: 0.82rem;
                    font-weight: 500;
                }

                .modern-table .col-client-hono-montant .hono-montant-central {
                    font-weight: 500;
                    color: #374151;
                }

                .modern-table .col-client-hono-type {
                    width: 105px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 0.9rem;
                }

                .modern-table thead th.col-client-hono-type {
                    color: #111827;
                    font-size: 0.82rem;
                    font-weight: 500;
                }

                .modern-table thead th.col-client-hono-montant,
                .modern-table thead th.col-client-hono-type {
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                }

                .modern-table .col-client-hono-type .hono-type-central {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 58px;
                    padding: 0.25rem 0.4rem;
                    border-radius: 6px;
                    background: #eef2ff;
                    font-weight: 600;
                    color: #1d4ed8;
                    font-size: 0.7rem;
                }

                .modern-table .col-client-nh-type {
                    width: 90px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 0.9rem;
                }

                .modern-table thead th.col-client-nh-type {
                    color: #111827;
                    font-size: 0.82rem;
                    font-weight: 500;
                }

                .nh-type-radio-group {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    align-items: center;
                }

                .nh-type-radio-item {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    cursor: pointer;
                }

                .nh-type-radio-item input[type="radio"] {
                    cursor: pointer;
                    width: 18px;
                    height: 18px;
                    accent-color: #667eea;
                }

                .nh-type-radio-item label {
                    margin: 0;
                    cursor: pointer;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .modern-table .col-client-hono-delete {
                    width: 220px;
                    min-width: 200px;
                    text-align: center;
                    vertical-align: middle;
                }

                .modern-table .btn-supprimer-hono {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    background: #ffffff;
                    color: #dc2626;
                    border: 1px solid #fee2e2;
                    border-radius: 8px;
                    padding: 0;
                    font-size: 0.9rem;
                    font-weight: 500;
                    cursor: pointer;
                    white-space: nowrap;
                    line-height: 1.2;
                }

                .modern-table .btn-supprimer-hono:hover:not(:disabled) {
                    background: #fef2f2;
                    color: #b91c1c;
                    text-decoration: none;
                }

                .modern-table .btn-supprimer-hono:disabled {
                    background: #ffffff;
                    color: #fca5a5;
                    opacity: 0.45;
                    cursor: not-allowed;
                }

                .modern-table .btn-modifier-client {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    background: #ffffff;
                    color: #2563eb;
                    border: 1px solid #dbeafe;
                    border-radius: 8px;
                    padding: 0;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
                }

                .modern-table .btn-modifier-client:hover {
                    background: #eff6ff;
                    color: #1d4ed8;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.16);
                }

                .modern-table .col-client-hono-delete .client-row-actions {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    flex-wrap: nowrap;
                }

                .modern-table .btn-voir-client {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    background: #ffffff;
                    color: #2563eb;
                    border: 1px solid #bfdbfe;
                    border-radius: 8px;
                    padding: 0;
                    cursor: pointer;
                    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
                }

                .modern-table .btn-voir-client:hover {
                    background: #eff6ff;
                    color: #1d4ed8;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12);
                }

                .nh-action-icon-btn--personnel {
                    border-color: #0d9488;
                    color: #0f766e;
                    min-width: auto;
                    width: auto;
                    padding: 0 0.45rem;
                    gap: 0.25rem;
                    font-size: 0.68rem;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .nh-action-icon-btn--personnel:hover {
                    background: #f0fdfa;
                    border-color: #0f766e;
                    color: #5b21b6;
                }

                .all-clients-page .nh-action-icons--split {
                    display: flex;
                    width: 100%;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.5rem;
                    flex-wrap: nowrap;
                }

                .all-clients-page .nh-action-icons-end {
                    display: inline-flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 4px;
                    margin-left: auto;
                    flex-shrink: 0;
                }

                .all-clients-page .col-client-hono-delete.nh-col-actions {
                    text-align: left;
                }

                .personnel-modal-plaque {
                    width: min(1100px, 98vw);
                    max-height: 94vh;
                    overflow: auto;
                    background: #fff;
                    border-radius: 12px;
                    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.22);
                    border: 1px solid #e2e8f0;
                }

                .personnel-modal-plaque .hono-modal-header {
                    padding: 0.7rem 1rem;
                }

                .personnel-modal-plaque .hono-modal-title {
                    font-size: 0.98rem;
                    line-height: 1.25;
                }

                .personnel-modal-plaque .hono-modal-body {
                    padding: 0.75rem 1rem 1rem;
                }

                .personnel-modal-plaque .hono-modal-helper {
                    font-size: 0.75rem;
                    padding: 0.4rem 0.55rem;
                    margin-bottom: 0.55rem;
                }

                .personnel-modal-plaque .hono-modal-label {
                    font-size: 0.68rem;
                    margin-bottom: 0.2rem;
                    letter-spacing: 0.03em;
                }

                .personnel-modal-plaque .hono-modal-input {
                    min-height: 32px;
                    height: 32px;
                    padding: 0.25rem 0.55rem;
                    font-size: 0.8rem;
                    border-radius: 7px;
                }

                .personnel-modal-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 0.45rem 0.65rem;
                }

                .personnel-modal-grid .personnel-field-full {
                    grid-column: span 1;
                }

                .personnel-modal-section {
                    margin: 0.65rem 0 0.3rem;
                    font-size: 0.7rem;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    color: #0f766e;
                }

                .personnel-modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.5rem;
                    margin-top: 0.85rem;
                }

                .personnel-modal-btn {
                    border: none;
                    border-radius: 7px;
                    padding: 0.45rem 0.95rem;
                    font-weight: 600;
                    font-size: 0.8rem;
                    cursor: pointer;
                }

                .personnel-modal-btn--cancel {
                    background: #f1f5f9;
                    color: #334155;
                }

                .personnel-modal-btn--save {
                    background: #0f766e;
                    color: #fff;
                }

                .personnel-modal-btn--save:disabled,
                .personnel-modal-btn--cancel:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                }

                @media (max-width: 900px) {
                    .personnel-modal-plaque {
                        width: min(96vw, 1100px);
                    }

                    .personnel-modal-grid {
                        grid-template-columns: 1fr 1fr;
                    }
                }

                @media (max-width: 640px) {
                    .personnel-modal-grid {
                        grid-template-columns: 1fr;
                    }

                    .personnel-modal-grid .personnel-field-full {
                        grid-column: 1 / -1;
                    }
                }

                .client-avatar {
                    display: inline-flex;
                    width: 34px;
                    height: 34px;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    border: 0;
                    cursor: pointer;
                    transition: transform 0.18s ease, box-shadow 0.18s ease;
                }

                .client-avatar:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.18);
                }

                .client-avatar.avatar-tone-0 { background: #f3e8ff; color: #0d9488; }
                .client-avatar.avatar-tone-1 { background: #dcfce7; color: #16a34a; }
                .client-avatar.avatar-tone-2 { background: #dbeafe; color: #2563eb; }
                .client-avatar.avatar-tone-3 { background: #ffedd5; color: #ea580c; }
                .client-avatar.avatar-tone-4 { background: #fce7f3; color: #db2777; }
                .client-avatar.avatar-tone-5 { background: #cffafe; color: #0891b2; }
                
                .modern-table tbody tr {
                    transition: background 0.1s ease;
                    cursor: default;
                }

                .modern-table tbody td.clickable-client-cell {
                    cursor: pointer;
                }

                .all-clients-page .nh-modern-data-table tbody td.clickable-client-cell {
                    cursor: pointer;
                }

                .all-clients-page .nh-modern-data-table tbody td.col-client-nom.clickable-client-cell {
                    color: #1d4ed8;
                }

                .all-clients-page .nh-modern-data-table tbody td.col-client-nom.clickable-client-cell:hover {
                    background: #eff6ff !important;
                    text-decoration: underline;
                }
                
                .modern-table tbody td.clickable-client-cell:hover {
                    background: #f8fafc !important;
                    color: #1d4ed8;
                }
                
                .modern-alert {
                    border-radius: 12px;
                    border: none;
                    padding: 1rem 1.5rem;
                    margin-bottom: 1.5rem;
                    font-weight: 500;
                }
                
                .modern-alert-success {
                    background: linear-gradient(45deg, #d4edda, #c3e6cb);
                    color: #155724;
                    border-left: 4px solid #28a745;
                }
                
                .modern-alert-danger {
                    background: linear-gradient(45deg, #f8d7da, #f5c6cb);
                    color: #721c24;
                    border-left: 4px solid #dc3545;
                }
                
                .modern-alert-info {
                    background: linear-gradient(45deg, #d1ecf1, #bee5eb);
                    color: #0c5460;
                    border-left: 4px solid #17a2b8;
                }
                
                .modern-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid #667eea;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    display: inline-block;
                    margin-right: 8px;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(15, 23, 42, 0.48);
                    backdrop-filter: blur(6px);
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                }

                .hono-modal-overlay {
                    z-index: 1100;
                }

                .client-confirm-overlay {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                    background: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(2px);
                    z-index: 1300;
                }

                .client-confirm-plaque {
                    width: auto;
                    max-width: 340px;
                    min-width: 280px;
                    margin: 0 auto;
                    padding: 1.1rem 1.15rem 1rem;
                    text-align: center;
                    border-radius: 12px;
                    background: #fff;
                    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.2);
                    border: 1.5px solid #99f6e4;
                    box-sizing: border-box;
                }

                .client-confirm-plaque--archive {
                    border-color: #99f6e4;
                }

                .client-confirm-plaque--restore {
                    border-color: #99f6e4;
                }

                .client-confirm-plaque--danger {
                    border-color: #fecaca;
                }

                .client-confirm-icon {
                    width: 42px;
                    height: 42px;
                    margin: 0 auto 0.65rem;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.05rem;
                }

                .client-confirm-plaque--archive .client-confirm-icon {
                    background: #f0fdfa;
                    color: #0f766e;
                }

                .client-confirm-plaque--restore .client-confirm-icon {
                    background: #f0fdfa;
                    color: #0d9488;
                }

                .client-confirm-plaque--danger .client-confirm-icon {
                    background: #fef2f2;
                    color: #dc2626;
                }

                .client-confirm-title {
                    font-size: 0.98rem;
                    font-weight: 700;
                    color: #1e1b4b;
                    margin: 0 0 0.4rem;
                }

                .client-confirm-message {
                    margin: 0 0 0.3rem;
                    font-size: 0.86rem;
                    font-weight: 600;
                    color: #334155;
                    line-height: 1.35;
                }

                .client-confirm-detail {
                    margin: 0 0 0.95rem;
                    font-size: 0.75rem;
                    color: #64748b;
                    line-height: 1.4;
                }

                .client-confirm-actions {
                    display: flex;
                    justify-content: center;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }

                .client-confirm-btn {
                    min-width: 100px;
                    min-height: 36px;
                    padding: 0.4rem 0.85rem;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 0.8rem;
                    cursor: pointer;
                    border: 1px solid transparent;
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }

                .client-confirm-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .client-confirm-btn--cancel {
                    background: #fff;
                    color: #64748b;
                    border-color: #cbd5e1;
                }

                .client-confirm-btn--cancel:hover:not(:disabled) {
                    background: #f8fafc;
                    border-color: #94a3b8;
                }

                .client-confirm-btn--ok.client-confirm-btn--archive {
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: #fff;
                }

                .client-confirm-btn--ok.client-confirm-btn--restore {
                    background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
                    color: #fff;
                }

                .client-confirm-btn--ok.client-confirm-btn--danger {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    color: #fff;
                }

                .client-confirm-btn--ok:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(15, 118, 110, 0.28);
                }

                .client-confirm-btn--ok.client-confirm-btn--danger:hover:not(:disabled) {
                    box-shadow: 0 6px 16px rgba(220, 38, 38, 0.28);
                }

                /* —— Modales clients : même design system violet que la liste —— */
                .client-details-card {
                    width: min(94vw, 680px);
                    background: #ffffff;
                    border-radius: 14px;
                    box-shadow: 0 20px 60px rgba(15, 118, 110, 0.22);
                    border: 1px solid #99f6e4;
                    overflow: hidden;
                    font-family: "Inter", "Segoe UI", Arial, sans-serif;
                    animation: modalSlideIn 0.25s ease-out;
                }

                .client-details-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.9rem 1.15rem;
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: #ffffff;
                }

                .client-details-title {
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    margin: 0;
                    font-size: 0.95rem;
                    font-weight: 700;
                    letter-spacing: 0.02em;
                    text-transform: uppercase;
                }

                .client-details-title .client-avatar {
                    width: 32px;
                    height: 32px;
                    background: rgba(255, 255, 255, 0.2);
                    color: #ffffff;
                    border-radius: 8px;
                    cursor: default;
                    box-shadow: none;
                }

                .client-details-close {
                    width: 32px;
                    height: 32px;
                    border: 1px solid rgba(255, 255, 255, 0.35);
                    border-radius: 8px;
                    background: rgba(255, 255, 255, 0.12);
                    color: #ffffff;
                    font-size: 1.15rem;
                    line-height: 1;
                    cursor: pointer;
                    transition: background 0.15s ease;
                }

                .client-details-close:hover {
                    background: rgba(255, 255, 255, 0.22);
                }

                .client-details-hero {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    padding: 1rem 1.15rem;
                    background: linear-gradient(135deg, #f8f7ff 0%, #eff6ff 55%, #ffffff 100%);
                    border-bottom: 1px solid #99f6e4;
                }

                .client-details-hero-avatar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 52px;
                    height: 52px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: #ffffff;
                    flex-shrink: 0;
                    box-shadow: 0 4px 14px rgba(15, 118, 110, 0.35);
                }

                .client-details-hero-name {
                    margin: 0 0 0.2rem;
                    font-size: 1.15rem;
                    font-weight: 700;
                    color: #1e293b;
                    word-break: break-word;
                }

                .client-details-hero-id {
                    display: inline-flex;
                    align-items: center;
                    padding: 2px 8px;
                    border-radius: 6px;
                    background: #f0fdfa;
                    border: 1px solid #99f6e4;
                    color: #0f766e;
                    font-size: 0.78rem;
                    font-weight: 700;
                }

                .client-details-body {
                    padding: 1rem 1.15rem 0.5rem;
                }

                .client-details-section {
                    margin-bottom: 1rem;
                }

                .client-details-section-title {
                    margin: 0 0 0.55rem;
                    padding-bottom: 0.35rem;
                    border-bottom: 1px solid #99f6e4;
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #0f766e;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .client-details-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 0.65rem;
                }

                .client-detail-item {
                    padding: 0.7rem 0.8rem;
                    background: #ffffff;
                    border: 1px solid #99f6e4;
                    border-radius: 10px;
                    min-height: 62px;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }

                .client-detail-item:hover {
                    border-color: #99f6e4;
                    box-shadow: 0 2px 8px rgba(15, 118, 110, 0.08);
                }

                .client-detail-item.full-width {
                    grid-column: 1 / -1;
                }

                .client-detail-label {
                    display: block;
                    color: #5b21b6;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    margin-bottom: 0.3rem;
                }

                .client-detail-value {
                    color: #1e293b;
                    font-size: 0.88rem;
                    font-weight: 500;
                    word-break: break-word;
                }

                .client-detail-value--amount {
                    color: #1d4ed8;
                    font-weight: 700;
                    font-size: 0.92rem;
                }

                .client-detail-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 42px;
                    padding: 3px 10px;
                    border-radius: 6px;
                    background: #f0fdfa;
                    color: #0f766e;
                    border: 1px solid #99f6e4;
                    font-size: 0.78rem;
                    font-weight: 700;
                }

                .client-detail-badge--nh {
                    background: #ecfdf5;
                    color: #047857;
                    border-color: #86efac;
                }

                .client-details-footer {
                    display: flex;
                    justify-content: flex-end;
                    padding: 0.75rem 1.15rem 1rem;
                    border-top: 1px solid #eef2f7;
                    background: #fafafa;
                }

                .client-details-btn-close {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 7px 16px;
                    border: none;
                    border-radius: 8px;
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: #ffffff;
                    font-size: 0.82rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: filter 0.15s ease, transform 0.1s ease;
                }

                .client-details-btn-close:hover {
                    filter: brightness(1.06);
                    transform: translateY(-1px);
                }

                @media (max-width: 640px) {
                    .client-details-card {
                        width: min(100%, 340px);
                        max-height: calc(100dvh - 0.75rem);
                        overflow: auto;
                        border-radius: 12px;
                    }

                    .all-clients-page .modal-overlay:has(.client-details-card) {
                        padding: 0.35rem;
                        align-items: flex-start;
                        padding-top: max(0.35rem, env(safe-area-inset-top));
                    }

                    .client-details-header {
                        padding: 0.55rem 0.7rem;
                    }

                    .client-details-title {
                        font-size: 0.72rem;
                        gap: 0.4rem;
                    }

                    .client-details-title .client-avatar {
                        width: 24px;
                        height: 24px;
                        border-radius: 6px;
                    }

                    .client-details-close {
                        width: 26px;
                        height: 26px;
                        font-size: 1rem;
                        border-radius: 6px;
                    }

                    .client-details-hero {
                        padding: 0.55rem 0.7rem;
                        gap: 0.5rem;
                    }

                    .client-details-hero-avatar {
                        width: 36px;
                        height: 36px;
                        border-radius: 8px;
                    }

                    .client-details-hero-avatar svg {
                        width: 18px;
                        height: 18px;
                    }

                    .client-details-hero-name {
                        font-size: 0.82rem;
                        margin-bottom: 0.12rem;
                    }

                    .client-details-hero-id {
                        font-size: 0.65rem;
                        padding: 1px 6px;
                    }

                    .client-details-body {
                        padding: 0.55rem 0.7rem 0.35rem;
                    }

                    .client-details-section {
                        margin-bottom: 0.55rem;
                    }

                    .client-details-section-title {
                        font-size: 0.58rem;
                        margin-bottom: 0.3rem;
                        padding-bottom: 0.22rem;
                    }

                    .client-details-grid {
                        grid-template-columns: 1fr 1fr;
                        gap: 0.35rem;
                    }

                    .client-detail-item {
                        padding: 0.35rem 0.45rem;
                        min-height: 0;
                        border-radius: 7px;
                    }

                    .client-detail-item.full-width {
                        grid-column: 1 / -1;
                    }

                    .client-detail-label {
                        font-size: 0.55rem;
                        margin-bottom: 0.12rem;
                    }

                    .client-detail-value {
                        font-size: 0.72rem;
                    }

                    .client-detail-value--amount {
                        font-size: 0.76rem;
                    }

                    .client-detail-badge {
                        min-width: 0;
                        padding: 2px 7px;
                        font-size: 0.68rem;
                    }

                    .client-details-footer {
                        padding: 0.45rem 0.7rem 0.55rem;
                    }

                    .client-details-btn-close {
                        padding: 5px 12px;
                        font-size: 0.72rem;
                        border-radius: 7px;
                    }

                    .client-details-btn-close:hover {
                        transform: none;
                    }
                }

                .hono-modal-plaque {
                    background: #ffffff;
                    color: #1e293b;
                    border-radius: 14px;
                    padding: 0;
                    max-width: 620px;
                    width: 100%;
                    box-shadow: 0 20px 60px rgba(15, 118, 110, 0.22);
                    border: 1px solid #99f6e4;
                    font-family: "Inter", "Segoe UI", Arial, sans-serif;
                    animation: modalSlideIn 0.25s ease-out;
                    overflow: hidden;
                }

                .hono-modal-plaque form {
                    display: flex;
                    flex-direction: column;
                }

                .hono-modal-header {
                    padding: 1rem 1.25rem;
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: #ffffff;
                    text-align: center;
                }

                .hono-modal-title {
                    display: block;
                    margin: 0 !important;
                    padding: 0 !important;
                    background: none !important;
                    color: #ffffff !important;
                    border-radius: 0 !important;
                    font-size: 1.02rem !important;
                    font-weight: 700 !important;
                    line-height: 1.35;
                }

                .hono-modal-body {
                    padding: 1.1rem 1.25rem 0.25rem;
                    background: linear-gradient(180deg, #f8f7ff 0%, #ffffff 45%);
                }

                .hono-modal-plaque .hono-modal-label {
                    display: block;
                    font-weight: 700;
                    font-size: 0.7rem;
                    margin-bottom: 0.45rem;
                    line-height: 1.35;
                    color: #5b21b6;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }

                .hono-modal-helper {
                    color: #64748b !important;
                    font-size: 0.82rem !important;
                    font-weight: 400 !important;
                    text-transform: none !important;
                    letter-spacing: normal !important;
                    margin-bottom: 0.85rem !important;
                    padding: 0.55rem 0.7rem;
                    background: #ffffff;
                    border: 1px solid #99f6e4;
                    border-radius: 8px;
                }

                .hono-modal-section-title {
                    margin: 0 0 0.5rem;
                    padding-bottom: 0.35rem;
                    border-bottom: 1px solid #99f6e4;
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #0f766e;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .hono-modal-readonly-notice {
                    background: #f0fdfa;
                    border: 1px solid #99f6e4;
                    color: #5b21b6;
                    border-radius: 8px;
                    font-size: 0.8rem;
                    padding: 0.55rem 0.7rem;
                    margin-bottom: 0.85rem;
                    line-height: 1.4;
                }

                .hono-modal-radio-item.is-locked {
                    opacity: 0.65;
                    cursor: not-allowed;
                }

                .hono-modal-plaque .hono-modal-input:disabled {
                    background: #f8fafc;
                    color: #64748b;
                    cursor: not-allowed;
                }

                .hono-modal-plaque .hono-modal-input {
                    width: 100%;
                    border: 1px solid #99f6e4;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 0.88rem;
                    background: #ffffff;
                    color: #1e293b;
                    margin-bottom: 0.75rem;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }

                .hono-modal-plaque .hono-modal-input:focus {
                    outline: none;
                    border-color: #0f766e;
                    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
                }

                .hono-modal-alert {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #991b1b;
                    border-radius: 8px;
                    font-size: 0.8rem;
                    padding: 0.55rem 0.7rem;
                    margin-bottom: 0.75rem;
                }

                .hono-modal-radio-group {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0.55rem;
                    margin-bottom: 0.85rem;
                }

                .hono-modal-radio-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.84rem;
                    font-weight: 500;
                    color: #334155;
                    background: #ffffff;
                    border: 1px solid #99f6e4;
                    border-radius: 8px;
                    padding: 0.6rem 0.7rem;
                    cursor: pointer;
                    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
                }

                .hono-modal-radio-item:hover:not(.is-locked) {
                    border-color: #99f6e4;
                    background: #faf8ff;
                }

                .hono-modal-radio-item:has(input:checked) {
                    border-color: #0f766e;
                    background: #f0fdfa;
                    box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.12);
                }

                .hono-modal-radio-item input {
                    accent-color: #0f766e;
                }

                .hono-modal-arret-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin: 0.5rem 0 0.65rem;
                    padding: 0.55rem 0.75rem;
                    border-radius: 8px;
                    cursor: pointer;
                    background: #f8fafc;
                    border: 1px solid #99f6e4;
                    transition: border-color 0.15s ease, background 0.15s ease;
                }

                .hono-modal-arret-label.is-active {
                    background: #fff7ed;
                    border-color: #fdba74;
                }

                .hono-modal-arret-label.is-locked {
                    cursor: default;
                    opacity: 0.65;
                }

                .hono-modal-arret-label input {
                    accent-color: #ea580c;
                    width: 17px;
                    height: 17px;
                }

                .hono-modal-arret-text {
                    font-weight: 600;
                    font-size: 0.84rem;
                    color: #475569;
                }

                .hono-modal-arret-label.is-active .hono-modal-arret-text {
                    color: #c2410c;
                }

                .hono-modal-arret-icon {
                    margin-left: auto;
                    color: #ea580c;
                    font-size: 0.95rem;
                }

                .hono-modal-label--arret {
                    color: #c2410c !important;
                }

                .hono-modal-input--arret {
                    border-color: #fdba74 !important;
                }

                .hono-modal-input--arret:disabled {
                    opacity: 0.55;
                }

                .hono-modal-footer {
                    padding: 0.85rem 1.25rem 1.1rem;
                    border-top: 1px solid #eef2f7;
                    background: #fafafa;
                }

                .hono-modal-actions {
                    display: flex;
                    gap: 0.55rem;
                    justify-content: flex-end;
                    flex-wrap: wrap;
                }

                .hono-modal-actions button {
                    border-radius: 8px;
                    padding: 8px 16px;
                    font-weight: 700;
                    font-size: 0.8rem;
                    border: none;
                    cursor: pointer;
                    min-width: 100px;
                    transition: filter 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
                }

                .hono-btn-modal-valider {
                    background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
                    color: #ffffff;
                }

                .hono-btn-modal-valider--arret {
                    background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%) !important;
                }

                .hono-btn-modal-annuler {
                    background: linear-gradient(135deg, #5a6268 0%, #495057 100%);
                    color: #ffffff;
                }

                .hono-btn-modal-renitialiser {
                    background: #ffffff;
                    color: #0f766e;
                    border: 1px solid #99f6e4 !important;
                }

                .hono-modal-actions button:hover:not(:disabled) {
                    filter: brightness(1.05);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
                }

                .hono-btn-modal-renitialiser:hover:not(:disabled) {
                    background: #f0fdfa;
                }

                .hono-modal-actions button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }

                .hono-modal-unlock {
                    width: 100%;
                    margin-top: 0.55rem;
                    padding: 0;
                    border: none;
                    background: none;
                    color: #0f766e;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-decoration: underline;
                    cursor: pointer;
                    text-align: center;
                }

                .hono-modal-unlock:hover {
                    color: #5b21b6;
                }

                .hono-cell-inner {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 6px;
                    max-width: 100%;
                }
                
                .form-container {
                    background: #ffffff;
                    border-radius: 14px;
                    padding: 0;
                    box-shadow: 0 16px 48px rgba(15, 118, 110, 0.18);
                    border: 1px solid #e9e5ff;
                    font-family: "Inter", "Segoe UI", Arial, sans-serif;
                    max-width: 520px;
                    width: 100%;
                    max-height: 90vh;
                    overflow: hidden;
                    position: relative;
                    animation: modalSlideIn 0.28s ease-out;
                    display: flex;
                    flex-direction: column;
                    text-transform: none;
                }
                
                @keyframes modalSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                .form-title {
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: #ffffff;
                    padding: 1rem 1.25rem;
                    border-radius: 0;
                    font-weight: 700;
                    font-size: 1rem;
                    margin: 0;
                    text-align: center;
                    flex-shrink: 0;
                }

                .form-container .add-form {
                    padding: 1rem 1.1rem 1.05rem;
                    overflow-y: auto;
                    flex: 1;
                    min-height: 0;
                }

                .form-title i {
                    font-size: 0.88rem;
                    opacity: 0.95;
                }

                .form-group {
                    margin-bottom: 0.55rem;
                }
                
                .form-label {
                    color: #1e293b;
                    font-weight: 600;
                    margin-bottom: 0.28rem;
                    display: block;
                    font-size: 0.78rem;
                }

                .form-label i {
                    font-size: 0.72rem;
                    color: #0f766e;
                    margin-right: 0.3rem !important;
                }
                
                .form-input {
                    width: 100%;
                    min-height: 34px;
                    border: 1px solid #99f6e4;
                    border-radius: 8px;
                    padding: 7px 10px;
                    font-size: 0.875rem;
                    line-height: 1.35;
                    color: #0f172a;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                    background: #ffffff;
                }

                .form-input::placeholder {
                    font-size: 0.8rem;
                    color: #94a3b8;
                }
                
                .form-input:focus {
                    outline: none;
                    border-color: #0f766e;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
                }
                
                .form-buttons {
                    display: flex;
                    gap: 0.65rem;
                    justify-content: center;
                    margin-top: 0.9rem;
                    padding-top: 0.65rem;
                    border-top: 1px solid #e2e8f0;
                }
                
                .form-btn {
                    border-radius: 8px;
                    padding: 9px 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    transition: all 0.15s ease;
                    border: none;
                    min-width: 115px;
                }

                .form-btn i {
                    font-size: 0.8rem;
                }
                
                .form-btn-secondary {
                    background: #94a3b8;
                    color: white;
                }
                
                .form-btn-primary {
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(15, 118, 110, 0.28);
                }
                
                .form-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.14);
                }
                
                .form-btn:disabled {
                    opacity: 0.7;
                    transform: none;
                }

                .form-type-section {
                    margin-top: 0.35rem;
                    padding: 0.65rem 0.75rem;
                    border: 1px solid #e9e5ff;
                    border-radius: 10px;
                    background: #f0fdfa;
                }

                .form-type-section > .row {
                    margin: 0;
                    display: flex;
                    flex-wrap: nowrap;
                    gap: 0.65rem;
                }

                .form-type-section > .row > [class*='col-'] {
                    flex: 1 1 50%;
                    max-width: 50%;
                    padding: 0;
                    min-width: 0;
                }

                .form-type-section .form-group {
                    margin-bottom: 0 !important;
                }

                .form-type-section .form-label {
                    font-size: 0.76rem;
                    margin-bottom: 0.35rem;
                    color: #115e59;
                }

                .form-container .row {
                    margin-left: -0.35rem;
                    margin-right: -0.35rem;
                }

                .form-container .row > [class*='col-'] {
                    padding-left: 0.35rem;
                    padding-right: 0.35rem;
                }

                .form-container .hono-modal-readonly-notice {
                    font-size: 0.75rem;
                    padding: 0.45rem 0.55rem;
                    margin-bottom: 0.4rem !important;
                }

                .form-radio-group {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 0.45rem;
                }

                .form-radio-card {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                    min-height: 38px;
                    padding: 0.4rem 0.5rem;
                    border: 1px solid #99f6e4;
                    border-radius: 8px;
                    background: #ffffff;
                    cursor: pointer;
                    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
                    margin: 0;
                    user-select: none;
                }

                .form-radio-card input[type="radio"] {
                    width: 16px;
                    height: 16px;
                    margin: 0;
                    cursor: pointer;
                    accent-color: #0f766e;
                    flex-shrink: 0;
                }

                .form-radio-card span {
                    font-size: 0.82rem;
                    font-weight: 600;
                    color: #1e293b;
                }

                .form-radio-card:hover {
                    border-color: #2dd4bf;
                    background: #f0fdfa;
                }

                .form-radio-card:has(input:checked) {
                    border-color: #0f766e;
                    background: #f0fdfa;
                    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
                }

                .form-radio-card:has(input:checked) span {
                    color: #115e59;
                }

                .form-radio-card.is-locked {
                    opacity: 0.75;
                    cursor: not-allowed;
                    pointer-events: none;
                }
                
                @media (max-width: 768px) {
                    .all-clients-title {
                        font-size: 2rem;
                    }
                    
                    .form-buttons {
                        flex-direction: row;
                        flex-wrap: wrap;
                        gap: 0.4rem;
                        margin-top: 0.65rem !important;
                    }

                    .all-clients-header,
                    .all-clients-actions,
                    .table-container {
                        margin: 0.5rem;
                        padding: 0.75rem;
                    }

                    /* Formulaires ajouter / modifier client (portable) */
                    .all-clients-page .modal-overlay {
                        padding: 0.35rem;
                        align-items: flex-start;
                        padding-top: max(0.35rem, env(safe-area-inset-top));
                    }

                    .all-clients-page .modal-overlay .form-container {
                        margin: 0 auto;
                        padding: 0;
                        max-width: min(100%, 340px);
                        width: 100%;
                        max-height: calc(100dvh - 0.75rem);
                        border-radius: 12px;
                        box-shadow: 0 10px 32px rgba(15, 118, 110, 0.2);
                    }

                    .all-clients-page .form-container .form-title {
                        font-size: 0.88rem;
                        padding: 0.65rem 0.75rem;
                        margin-bottom: 0;
                        border-radius: 0;
                    }

                    .all-clients-page .form-container .add-form {
                        padding: 0.45rem 0.5rem 0.55rem;
                    }

                    .all-clients-page .form-container .form-title i {
                        font-size: 0.75rem;
                        margin-right: 0.25rem !important;
                    }

                    .all-clients-page .form-container .form-group {
                        margin-bottom: 0.4rem !important;
                    }

                    .all-clients-page .form-container .form-label {
                        font-size: 0.7rem;
                        margin-bottom: 0.15rem;
                        font-weight: 600;
                    }

                    .all-clients-page .form-container .form-label i {
                        font-size: 0.65rem;
                        margin-right: 0.2rem !important;
                    }

                    .all-clients-page .form-container .form-input {
                        padding: 6px 8px;
                        font-size: 0.78rem;
                        border-radius: 6px;
                    }

                    .all-clients-page .form-container .form-type-section {
                        padding: 0.35rem 0.45rem;
                        margin-top: 0.15rem;
                        border-radius: 7px;
                    }

                    .all-clients-page .form-container .form-type-section .form-group {
                        margin-bottom: 0.3rem !important;
                    }

                    .all-clients-page .form-container .form-radio-group {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 0.25rem;
                    }

                    .all-clients-page .form-container .form-radio-card {
                        min-height: 28px;
                        padding: 0.22rem 0.3rem;
                        border-radius: 6px;
                        gap: 0.25rem;
                    }

                    .all-clients-page .form-container .form-radio-card input[type="radio"] {
                        width: 14px;
                        height: 14px;
                    }

                    .all-clients-page .form-container .form-radio-card span {
                        font-size: 0.68rem;
                    }

                    .all-clients-page .form-container .form-btn {
                        min-width: 0;
                        flex: 1 1 auto;
                        padding: 7px 10px;
                        font-size: 0.75rem;
                        border-radius: 7px;
                    }

                    .all-clients-page .form-container .form-btn:hover {
                        transform: none;
                    }

                    .all-clients-page .form-container .row {
                        margin-left: 0;
                        margin-right: 0;
                    }

                    .all-clients-page .form-container .row > [class*='col-'] {
                        padding-left: 0;
                        padding-right: 0;
                    }

                    .all-clients-page .form-container .modern-alert {
                        padding: 0.45rem 0.55rem;
                        margin-bottom: 0.45rem;
                        font-size: 0.72rem;
                        border-radius: 7px;
                    }

                    /* Modale gérer hono (portable) */
                    .all-clients-page .hono-modal-overlay {
                        padding: 0.35rem;
                        align-items: flex-start;
                        padding-top: max(0.35rem, env(safe-area-inset-top));
                    }

                    .all-clients-page .hono-modal-plaque {
                        max-width: min(100%, 340px);
                        margin: 0 auto;
                        max-height: calc(100dvh - 0.75rem);
                        overflow: auto;
                    }

                    .all-clients-page .hono-modal-header {
                        padding: 0.6rem 0.75rem;
                    }

                    .all-clients-page .hono-modal-title {
                        font-size: 0.82rem !important;
                    }

                    .all-clients-page .hono-modal-body {
                        padding: 0.55rem 0.65rem 0.15rem;
                    }

                    .all-clients-page .hono-modal-helper {
                        font-size: 0.72rem !important;
                        padding: 0.4rem 0.5rem !important;
                        margin-bottom: 0.5rem !important;
                    }

                    .all-clients-page .hono-modal-section-title {
                        font-size: 0.62rem;
                        margin-bottom: 0.35rem;
                    }

                    .all-clients-page .hono-modal-plaque .hono-modal-label {
                        font-size: 0.62rem;
                        margin-bottom: 0.25rem;
                    }

                    .all-clients-page .hono-modal-plaque .hono-modal-input {
                        padding: 6px 8px;
                        font-size: 0.78rem;
                        min-height: 32px;
                    }

                    .all-clients-page .hono-modal-radio-group {
                        gap: 0.3rem;
                    }

                    .all-clients-page .hono-modal-radio-item {
                        padding: 0.3rem 0.35rem;
                        min-height: 0;
                        font-size: 0.68rem;
                    }

                    .all-clients-page .hono-modal-radio-item input {
                        width: 14px;
                        height: 14px;
                    }

                    .all-clients-page .hono-modal-arret-label {
                        padding: 0.35rem 0.45rem;
                        margin-bottom: 0.45rem;
                    }

                    .all-clients-page .hono-modal-arret-text {
                        font-size: 0.72rem;
                    }

                    .all-clients-page .hono-modal-footer {
                        padding: 0.5rem 0.65rem 0.65rem;
                    }

                    .all-clients-page .hono-modal-actions {
                        gap: 0.35rem;
                    }

                    .all-clients-page .hono-modal-actions button {
                        padding: 7px 10px;
                        font-size: 0.72rem;
                        min-width: 0;
                        flex: 1 1 auto;
                    }

                    .all-clients-page .modal-overlay .form-container {
                        max-width: min(100%, 340px);
                    }

                    /* Tableau clients nh-modern (portable) */
                    .all-clients-page .clients-modern-table-card .nh-modern-table-toolbar {
                        padding: 8px 10px 6px;
                        gap: 8px;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-table-length {
                        font-size: 0.72rem;
                        gap: 6px;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-table-length select {
                        min-width: 52px;
                        padding: 4px 22px 4px 8px;
                        font-size: 0.72rem;
                    }

                    .all-clients-page .clients-modern-table-card .clients-table-toolbar__actions .cgm-toolbar-btn {
                        min-height: 32px;
                        padding: 5px 10px;
                        font-size: 0.72rem;
                    }

                    .all-clients-page .clients-modern-table-card {
                        overflow: visible;
                    }

                    .all-clients-page .clients-modern-table-card .overview-table-container.nh-modern-table-scroll,
                    .all-clients-page .clients-modern-table-card .nh-modern-table-scroll {
                        overflow-x: auto !important;
                        overflow-y: visible;
                        width: 100%;
                        max-width: 100%;
                        -webkit-overflow-scrolling: touch;
                        touch-action: pan-x pan-y;
                        overscroll-behavior-y: auto;
                    }

                    .all-clients-page .clients-modern-table-card .overview-table.nh-overview-data-table.nh-modern-data-table {
                        width: max-content !important;
                        min-width: 780px !important;
                        table-layout: auto !important;
                    }

                    .all-clients-page .clients-modern-table-card .overview-table.nh-overview-data-table.nh-modern-data-table {
                        width: max-content !important;
                        min-width: 780px !important;
                        table-layout: auto !important;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table {
                        table-layout: auto;
                        width: max-content !important;
                        min-width: 780px !important;
                        font-size: 0.58rem !important;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table thead th {
                        white-space: nowrap !important;
                        padding: 0.35rem 0.45rem !important;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table thead th,
                    .all-clients-page .clients-modern-table-card .nh-modern-data-table tbody td {
                        padding: 0.28rem 0.4rem !important;
                        font-size: 0.56rem !important;
                        line-height: 1.15;
                        vertical-align: middle;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-sort-icons {
                        display: none !important;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-username {
                        min-width: 42px;
                        width: 42px;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-nom {
                        min-width: 100px;
                        width: 120px;
                        max-width: 140px !important;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        text-align: left;
                        font-weight: 600;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-action {
                        min-width: 58px;
                        width: auto;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-montant {
                        min-width: 78px;
                        width: auto;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-type {
                        min-width: 72px;
                        width: auto;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-facturation {
                        min-width: 72px;
                        width: auto;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-dernier-fact {
                        min-width: 88px;
                        width: auto;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-data-table .col-client-hono-delete {
                        min-width: 180px;
                        width: auto;
                        text-align: center;
                    }

                    .all-clients-page .clients-modern-table-card thead th.col-client-hono-action .nh-modern-th-inner,
                    .all-clients-page .clients-modern-table-card thead th.col-client-hono-montant .nh-modern-th-inner,
                    .all-clients-page .clients-modern-table-card thead th.col-client-hono-type .nh-modern-th-inner {
                        font-size: inherit;
                        line-height: inherit;
                    }

                    .all-clients-page .clients-modern-table-card thead th.col-client-hono-action .nh-modern-th-inner::after,
                    .all-clients-page .clients-modern-table-card thead th.col-client-hono-montant .nh-modern-th-inner::after,
                    .all-clients-page .clients-modern-table-card thead th.col-client-hono-type .nh-modern-th-inner::after {
                        content: none;
                    }

                    .all-clients-page .clients-modern-table-card thead th.col-client-nom .nh-modern-th-inner {
                        font-size: 0.56rem;
                    }

                    .all-clients-page .clients-modern-table-card thead th.col-client-username .nh-modern-th-inner {
                        font-size: 0.56rem;
                    }

                    .all-clients-page .clients-hono-manage-btn {
                        padding: 2px 3px;
                        font-size: 0;
                        gap: 0;
                        border-radius: 4px;
                        min-width: 22px;
                        justify-content: center;
                    }

                    .all-clients-page .clients-hono-manage-btn svg {
                        width: 10px;
                        height: 10px;
                    }

                    .all-clients-page .client-facture-badge {
                        padding: 1px 4px;
                        font-size: 0.5rem;
                    }

                    .all-clients-page .clients-modern-table-card .nh-badge-amount,
                    .all-clients-page .clients-modern-table-card .hono-montant-link,
                    .all-clients-page .clients-modern-table-card .hono-type-central,
                    .all-clients-page .clients-modern-table-card .nh-badge-taux {
                        font-size: 0.5rem !important;
                        padding: 1px 3px !important;
                    }

                    .all-clients-page .clients-modern-table-card .client-row-actions .btn-modifier-client,
                    .all-clients-page .clients-modern-table-card .client-row-actions .btn-voir-client,
                    .all-clients-page .clients-modern-table-card .client-row-actions .btn-supprimer-hono {
                        width: 20px !important;
                        height: 20px !important;
                        min-width: 20px !important;
                        padding: 0 !important;
                    }

                    .all-clients-page .clients-modern-table-card .client-row-actions .btn-modifier-client svg,
                    .all-clients-page .clients-modern-table-card .client-row-actions .btn-voir-client svg,
                    .all-clients-page .clients-modern-table-card .client-row-actions .btn-supprimer-hono svg {
                        width: 10px !important;
                        height: 10px !important;
                    }

                    .all-clients-page .clients-modern-table-card .client-row-actions {
                        gap: 2px;
                    }

                    .all-clients-page .clients-modern-table-card .nh-modern-table-footer {
                        padding: 8px 10px;
                        font-size: 0.72rem;
                    }

                    /* —— Barre d’actions clients (portable) —— */
                    .clients-table-actions,
                    .clients-table-actions--edit {
                        display: flex;
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.35rem;
                        margin-bottom: 0.5rem;
                    }

                    .clients-table-actions .actions-grid {
                        display: flex;
                        flex-direction: row;
                        flex-wrap: nowrap;
                        gap: 0.35rem;
                        width: 100%;
                        margin: 0;
                        justify-content: flex-start;
                        order: 1;
                    }

                    .clients-table-actions .actions-grid .action-btn-danger {
                        justify-self: auto;
                        margin-left: 0 !important;
                    }

                    /* Portable : icônes seules (Ajouter, Modifier, Supprimer) */
                    .clients-table-actions .action-btn-label {
                        display: none !important;
                    }

                    .clients-table-actions .uiverse-action-button,
                    .clients-table-actions .action-btn-danger {
                        width: 36px !important;
                        min-width: 36px !important;
                        height: 36px !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        border-radius: 8px;
                        flex: 0 0 auto;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .clients-table-actions .uiverse-action-button svg,
                    .clients-table-actions .uiverse-action-button i,
                    .clients-table-actions .action-btn-icon-svg {
                        width: 17px !important;
                        height: 17px !important;
                        margin: 0 !important;
                        flex-shrink: 0;
                        display: block;
                    }

                    .clients-search-count-wrap {
                        order: 2;
                        flex: 0 0 auto;
                        flex-direction: row;
                        align-items: center;
                        gap: 0.3rem;
                        width: 100%;
                    }

                    .clients-search-count-wrap .search-container {
                        flex: 1 1 auto;
                        min-width: 0;
                        max-width: calc(100% - 5.5rem);
                        padding: 0.1rem;
                        border-radius: 7px;
                    }

                    .clients-search-count-wrap .search-box {
                        min-height: 28px;
                    }

                    .clients-search-count-wrap .search-icon-box {
                        width: 26px;
                    }

                    .clients-search-count-wrap .search-icon-box svg {
                        width: 12px;
                    }

                    .clients-search-count-wrap .search-input {
                        font-size: 0.68rem;
                        padding: 0 0.35rem;
                    }

                    .clients-search-count-wrap .search-submit-btn {
                        min-width: 36px;
                        padding: 0 0.35rem;
                        font-size: 0;
                    }

                    .clients-search-count-wrap .search-submit-btn::after {
                        content: '⌕';
                        font-size: 0.8rem;
                        font-weight: 700;
                    }

                    .clients-count-badge {
                        flex: 0 0 auto;
                        padding: 0.22rem 0.4rem;
                        font-size: 0.65rem;
                        border-radius: 7px;
                        max-width: 5.25rem;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .clients-count-badge strong {
                        font-size: 0.75rem;
                    }

                    .clients-filter-label-short {
                        display: inline;
                    }

                    .clients-filter-label-long {
                        display: none;
                    }

                    .clients-table-filters-bar {
                        padding: 0.35rem 0.4rem;
                        margin-bottom: 0.45rem;
                        border-radius: 8px;
                        overflow: visible;
                    }

                    .clients-table-filters-inline {
                        display: grid;
                        grid-template-columns: repeat(3, minmax(0, 1fr));
                        gap: 0.3rem 0.35rem;
                        width: 100%;
                        flex-wrap: wrap;
                        justify-content: stretch;
                    }

                    .clients-filter-group {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.12rem;
                        min-width: 0;
                        white-space: normal;
                    }

                    .clients-filter-group label {
                        font-size: 0.6rem;
                        line-height: 1.1;
                    }

                    .clients-filter-select {
                        min-width: 0;
                        max-width: 100%;
                        width: 100%;
                        font-size: 0.65rem;
                        padding: 0.22rem 0.28rem;
                        border-radius: 6px;
                    }

                    .clients-filters-reset {
                        grid-column: 1 / -1;
                        width: 100%;
                        font-size: 0.65rem;
                        padding: 0.28rem 0.4rem;
                        border-radius: 6px;
                    }
                    
                    .action-btn-inline {
                        padding: 6px 10px !important;
                        font-size: 0.75rem !important;
                        min-width: 70px;
                    }
                    
                    .action-btn-inline i {
                        font-size: 0.75rem !important;
                        margin-right: 3px !important;
                    }
                    
                    .table-container {
                        max-width: 100%;
                        padding: 0.35rem 0.5rem 0.5rem;
                    }

                    /* Tableau clients compact — toutes les colonnes visibles */
                    .all-clients-page .table-container .table-responsive {
                        overflow-x: visible;
                        margin: 0 -0.15rem;
                    }

                    .all-clients-page .modern-table {
                        width: 100% !important;
                        min-width: 0 !important;
                        table-layout: fixed;
                        font-size: 0.58rem;
                    }

                    .all-clients-page .modern-table thead th,
                    .all-clients-page .modern-table tbody td {
                        padding: 0.2rem 0.1rem !important;
                        font-size: 0.56rem !important;
                        line-height: 1.15;
                        vertical-align: middle;
                        min-width: 0 !important;
                    }

                    .all-clients-page .modern-table tbody td {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .all-clients-page .modern-table .col-client-avatar {
                        width: 9% !important;
                        text-align: center;
                        padding: 0.12rem 0.05rem !important;
                    }

                    .all-clients-page .modern-table .col-client-username {
                        width: 11% !important;
                        text-align: center;
                        font-size: 0.54rem !important;
                    }

                    .all-clients-page .modern-table .col-client-nom {
                        width: 24% !important;
                        max-width: 0;
                    }

                    .all-clients-page .modern-table .col-client-hono-action {
                        width: 12% !important;
                        padding: 0.1rem !important;
                    }

                    .all-clients-page .modern-table .col-client-hono-montant {
                        width: 18% !important;
                        text-align: center;
                        font-size: 0.54rem !important;
                    }

                    .all-clients-page .modern-table .col-client-hono-type {
                        width: 26% !important;
                        text-align: center;
                        font-size: 0.52rem !important;
                    }

                    .all-clients-page .modern-table .col-client-hono-delete {
                        width: 10% !important;
                        padding: 0.1rem !important;
                    }

                    .all-clients-page .modern-table thead th.col-client-hono-action,
                    .all-clients-page .modern-table thead th.col-client-hono-montant,
                    .all-clients-page .modern-table thead th.col-client-hono-type {
                        text-transform: none;
                        letter-spacing: 0;
                        font-size: 0 !important;
                        line-height: 0;
                        padding: 0.25rem 0.08rem !important;
                    }

                    .all-clients-page .modern-table thead th.col-client-hono-action::after {
                        content: 'Hono.';
                        font-size: 0.5rem;
                        line-height: 1.2;
                        display: block;
                        color: #0f172a;
                        font-weight: 600;
                    }

                    .all-clients-page .modern-table thead th.col-client-hono-montant::after {
                        content: 'Mnt';
                        font-size: 0.5rem;
                        line-height: 1.2;
                        display: block;
                        color: #0f172a;
                        font-weight: 600;
                    }

                    .all-clients-page .modern-table thead th.col-client-hono-type::after {
                        content: 'Type';
                        font-size: 0.5rem;
                        line-height: 1.2;
                        display: block;
                        color: #0f172a;
                        font-weight: 600;
                    }

                    .all-clients-page .modern-table .client-avatar {
                        width: 22px;
                        height: 22px;
                        border-radius: 5px;
                    }

                    .all-clients-page .modern-table .client-avatar svg {
                        width: 11px !important;
                        height: 11px !important;
                    }

                    .all-clients-page .modern-table .btn-gerer-hono.uiverse-table-button {
                        min-width: 0 !important;
                        width: 22px !important;
                        height: 22px !important;
                        padding: 0 !important;
                        margin: 0 auto;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .all-clients-page .modern-table .btn-gerer-hono span {
                        font-size: 0 !important;
                        line-height: 0;
                        width: 0;
                        overflow: hidden;
                    }

                    .all-clients-page .modern-table .btn-gerer-hono span svg {
                        width: 11px !important;
                        height: 11px !important;
                        margin: 0 !important;
                    }

                    .all-clients-page .modern-table .hono-montant-central,
                    .all-clients-page .modern-table .hono-type-central {
                        font-size: 0.52rem !important;
                        font-weight: 600;
                        display: block;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .all-clients-page .modern-table .hono-type-central {
                        text-transform: capitalize;
                    }

                    .all-clients-page .modern-table .btn-supprimer-hono,
                    .all-clients-page .modern-table .btn-modifier-client,
                    .all-clients-page .modern-table .btn-voir-client {
                        width: 22px;
                        height: 22px;
                        border-radius: 5px;
                    }

                    .all-clients-page .modern-table .btn-supprimer-hono svg,
                    .all-clients-page .modern-table .btn-modifier-client svg,
                    .all-clients-page .modern-table .btn-voir-client svg {
                        width: 11px !important;
                        height: 11px !important;
                    }

                    .all-clients-page .modern-table .col-client-hono-delete .client-row-actions {
                        gap: 3px;
                    }

                    .all-clients-page .modern-table .hono-cell-inner {
                        display: flex;
                        justify-content: center;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-avatar {
                        width: 8% !important;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-username {
                        width: 10% !important;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-nom {
                        width: 14% !important;
                        max-width: 140px !important;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-hono-action {
                        width: 10% !important;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-hono-montant {
                        width: 12% !important;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-hono-type {
                        width: 14% !important;
                    }

                    .all-clients-page .modern-table:has(th.col-client-hono-delete) .col-client-hono-delete {
                        width: 22% !important;
                        min-width: 180px !important;
                    }

                    .all-clients-page .modern-table thead th.col-client-hono-delete {
                        font-size: 0 !important;
                    }

                    .all-clients-page .modern-table thead th.col-client-hono-delete::after {
                        content: 'Act.';
                        font-size: 0.55rem;
                        line-height: 1.2;
                        display: block;
                        font-weight: 600;
                        color: #0f172a;
                    }
                }
                
                @media (max-width: 480px) {
                    .clients-search-count-wrap .search-submit-btn {
                        min-width: 40px;
                        padding: 0 0.35rem;
                        font-size: 0;
                    }

                    .clients-search-count-wrap .search-submit-btn::after {
                        content: '⌕';
                        font-size: 0.85rem;
                        font-weight: 700;
                    }

                    .clients-count-badge {
                        flex: 0 0 auto;
                        max-width: 38%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .search-container {
                        margin: 0;
                        max-width: 100%;
                        padding: 0.12rem;
                    }
                    
                    .search-input {
                        font-size: 0.7rem;
                        padding: 0 0.35rem;
                    }
                    
                    .table-container {
                        max-width: 100%;
                        overflow-x: visible;
                    }

                    .all-clients-page .modern-table thead th,
                    .all-clients-page .modern-table tbody td {
                        padding: 0.15rem 0.06rem !important;
                        font-size: 0.52rem !important;
                    }

                    .all-clients-page .modern-table .col-client-nom {
                        width: 22% !important;
                    }

                    .all-clients-page .modern-table .col-client-hono-type {
                        width: 24% !important;
                    }
                    
                    /* Encore plus compact sur très petits écrans */
                    .action-btn {
                        padding: 5px 8px !important;
                        font-size: 0.65rem !important;
                    }
                    
                    .action-btn i {
                        font-size: 0.6rem !important;
                        margin-right: 2px !important;
                    }
                    
                    /* Boutons dans les lignes - toujours visibles même sur très petits écrans */
                    .action-btn-inline {
                        padding: 5px 8px !important;
                        font-size: 0.7rem !important;
                        min-width: 60px;
                    }
                    
                    .action-btn-inline i {
                        font-size: 0.7rem !important;
                        margin-right: 2px !important;
                    }

                    .clients-table-actions .actions-grid {
                        flex-direction: row;
                    }
                        /* Modern Checkbox Toggle */
.modern-checkbox-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    cursor: pointer;
    user-select: none;
    padding: 0;
    border-radius: 0;
    background: transparent;
    transition: none;
}

.modern-checkbox-wrapper input[type="checkbox"] {
    display: none;
}

.checkbox-toggle {
    position: relative;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    margin: 0;
    transition: transform 0.2s ease;
}

.checkbox-slider {
    position: absolute;
    inset: 0;
    width: auto;
    height: auto;
    border-radius: 6px;
    border: 2px solid #93c5fd;
    background: white;
    box-shadow: none;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.checkbox-slider::after {
    content: '✓';
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    transform: scale(0);
    transition: transform 0.2s ease;
}

.checkbox-label {
    font-weight: 500;
    font-size: 0.7rem;
    color: #4b5563;
    transition: none;
    min-width: 18px;
    text-align: left;
    display: inline-block;
}

/* Checked State */
.modern-checkbox-wrapper input[type="checkbox"]:checked + .checkbox-toggle {
    animation: checkmarkBounce 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-checkbox-wrapper input[type="checkbox"]:checked + .checkbox-toggle .checkbox-slider {
    background: #ef4444;
    border-color: #ef4444;
}

.modern-checkbox-wrapper input[type="checkbox"]:checked + .checkbox-toggle .checkbox-slider::after {
    transform: scale(1);
}

.modern-checkbox-wrapper input[type="checkbox"]:checked ~ .checkbox-label {
    color: #2563eb;
    font-weight: 600;
}

/* Hover Effects */
.modern-checkbox-wrapper:hover {
    background: transparent;
    transform: none;
}

.modern-checkbox-wrapper:hover .checkbox-toggle {
    transform: scale(1.05);
}

.modern-checkbox-wrapper input[type="checkbox"]:checked + .checkbox-toggle:hover {
    box-shadow: none;
}

/* Focus Effect */
.modern-checkbox-wrapper input[type="checkbox"]:focus + .checkbox-toggle {
    box-shadow: 0 0 0 4px #fee2e2;
}

/* Animation keyframes */
@keyframes checkmarkBounce {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
    }
}

/* Table cell styling */
.col-client-nh-type {
    text-align: center;
    padding: 12px 8px;
}

/* Responsive */
@media (max-width: 768px) {
    .modern-checkbox-wrapper {
        padding: 0;
    }
    
    .checkbox-toggle {
        width: 24px;
        height: 24px;
    }
    
    .checkbox-slider {
        width: auto;
        height: auto;
    }
    
    .checkbox-slider::after {
        font-size: 16px;
    }
    
    .checkbox-label {
        font-size: 11px;
    }
}
                }
            `}</style>

            <div className="all-clients-page" style={{ paddingTop: '0', marginTop: '0' }}>
                <div className="container" style={{ paddingTop: '0', marginTop: '0', maxWidth: 'none', width: '100%' }}>
                    {/* Header */}
                    <div className="all-clients-header">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h1 className="all-clients-title">
                                    <i className="fas fa-users me-3"></i>
                                    Tous les Clients
                                </h1>
                                <p className="all-clients-subtitle">
                                    Gestion complète de tous les clients
                                </p>
                            </div>
                            <Link to="/" className="modern-back-btn">
                                <i className="fas fa-arrow-left"></i>
                                Retour
                            </Link>
                        </div>
                    </div>


                    {/* Messages */}
                    {successMessage && (
                        <div className="modern-alert modern-alert-success">
                            <i className="fas fa-check-circle me-2"></i>
                            {successMessage}
                        </div>
                    )}

                    {error && (
                        <div className="modern-alert modern-alert-danger">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    {detailsClient && (
                        <div className="modal-overlay hono-modal-overlay" onClick={() => setDetailsClient(null)}>
                            <div className="client-details-card" onClick={(e) => e.stopPropagation()}>
                                <div className="client-details-header">
                                    <h5 className="client-details-title">
                                        <span className="client-avatar">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="18" height="18" fill="currentColor" aria-hidden="true">
                                                <path d="M240 192C240 147.8 275.8 112 320 112C364.2 112 400 147.8 400 192C400 236.2 364.2 272 320 272C275.8 272 240 236.2 240 192zM448 192C448 121.3 390.7 64 320 64C249.3 64 192 121.3 192 192C192 262.7 249.3 320 320 320C390.7 320 448 262.7 448 192zM144 544C144 473.3 201.3 416 272 416L368 416C438.7 416 496 473.3 496 544L496 552C496 565.3 506.7 576 520 576C533.3 576 544 565.3 544 552L544 544C544 446.8 465.2 368 368 368L272 368C174.8 368 96 446.8 96 544L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 544z" />
                                            </svg>
                                        </span>
                                        Données du client
                                    </h5>
                                    <button type="button" className="client-details-close" onClick={() => setDetailsClient(null)} aria-label="Fermer">×</button>
                                </div>
                                <div className="client-details-hero">
                                    <div className="client-details-hero-avatar" aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="26" height="26" fill="currentColor">
                                            <path d="M240 192C240 147.8 275.8 112 320 112C364.2 112 400 147.8 400 192C400 236.2 364.2 272 320 272C275.8 272 240 236.2 240 192zM448 192C448 121.3 390.7 64 320 64C249.3 64 192 121.3 192 192C192 262.7 249.3 320 320 320C390.7 320 448 262.7 448 192zM144 544C144 473.3 201.3 416 272 416L368 416C438.7 416 496 473.3 496 544L496 552C496 565.3 506.7 576 520 576C533.3 576 544 565.3 544 552L544 544C544 446.8 465.2 368 368 368L272 368C174.8 368 96 446.8 96 544L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 544z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="client-details-hero-name">{getClientNomComplet(detailsClient.client) || '—'}</h3>
                                        <span className="client-details-hero-id">ID {detailsClient.client.username || '—'}</span>
                                    </div>
                                </div>
                                <div className="client-details-body">
                                    <div className="client-details-section">
                                        <div className="client-details-section-title">Coordonnées</div>
                                        <div className="client-details-grid">
                                            <div className="client-detail-item full-width">
                                                <span className="client-detail-label">Adresse</span>
                                                <span className="client-detail-value">{detailsClient.client.adresse || '—'}</span>
                                            </div>
                                            <div className="client-detail-item">
                                                <span className="client-detail-label">Téléphone</span>
                                                <span className="client-detail-value">{detailsClient.client.telephone || '—'}</span>
                                            </div>
                                            <div className="client-detail-item">
                                                <span className="client-detail-label">Email</span>
                                                <span className="client-detail-value">{detailsClient.client.email || '—'}</span>
                                            </div>
                                            <div className="client-detail-item full-width">
                                                <span className="client-detail-label">Code TVA / Matricule Fiscal</span>
                                                <span className="client-detail-value">{detailsClient.client.matricule_fiscal || '—'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="client-details-section">
                                        <div className="client-details-section-title">Honoraires &amp; classification</div>
                                        <div className="client-details-grid">
                                                                                      <div className="client-detail-item">
                                                <span className="client-detail-label">Type</span>
                                                <span className="client-detail-badge">
                                                    {detailsClient.personneType === 'physique' ? 'P.P' : detailsClient.personneType === 'morale' ? 'P.M' : '—'}
                                                </span>
                                            </div>
                                            <div className="client-detail-item">
                                                <span className="client-detail-label">NH/E</span>
                                                <span className="client-detail-badge client-detail-badge--nh">{detailsClient.nhType || 'E'}</span>
                                            </div>
                                            <div className="client-detail-item">
                                                <span className="client-detail-label">Montant Hono</span>
                                                <span className="client-detail-value client-detail-value--amount">{detailsClient.montantHono || '—'}</span>
                                            </div>
                                            <div className="client-detail-item">
                                                <span className="client-detail-label">Type d'hono</span>
                                                <span className="client-detail-value">{detailsClient.typeHono || '—'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="client-details-footer">
                                    <button type="button" className="client-details-btn-close" onClick={() => setDetailsClient(null)}>
                                        <i className="fas fa-times" aria-hidden="true" />
                                        Fermer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {confirmModal && confirmModalCopy && (
                        <div
                            className="modal-overlay hono-modal-overlay client-confirm-overlay"
                            role="presentation"
                            onClick={closeConfirmModal}
                        >
                            <div
                                className={`client-confirm-plaque client-confirm-plaque--${confirmModalCopy.tone}`}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="client-confirm-title"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="client-confirm-icon" aria-hidden="true">
                                    <i className={`fas ${confirmModalCopy.icon}`} />
                                </div>
                                <div id="client-confirm-title" className="client-confirm-title">
                                    {confirmModalCopy.title}
                                </div>
                                <p className="client-confirm-message">{confirmModalCopy.message}</p>
                                <p className="client-confirm-detail">{confirmModalCopy.detail}</p>
                                <div className="client-confirm-actions">
                                    <button
                                        type="button"
                                        className="client-confirm-btn client-confirm-btn--cancel"
                                        onClick={closeConfirmModal}
                                        disabled={confirmBusy}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        className={`client-confirm-btn client-confirm-btn--ok client-confirm-btn--${confirmModalCopy.tone}`}
                                        onClick={executeConfirmModal}
                                        disabled={confirmBusy}
                                    >
                                        {confirmBusy ? 'Patientez…' : confirmModalCopy.confirmLabel}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {transferClient ? (
                        <div
                            className="modal-overlay hono-modal-overlay"
                            role="presentation"
                            onClick={() => !transferBusy && setTransferClient(null)}
                        >
                            <div
                                className="hono-modal-plaque client-transfer-plaque"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="client-transfer-title"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="hono-modal-header">
                                    <div id="client-transfer-title" className="hono-modal-title">
                                        Envoyer vers un cabinet
                                    </div>
                                </div>
                                <div className="hono-modal-body">
                                    <p className="hono-modal-helper">
                                        Déplacer <strong>{transferClient.nom || 'ce client'}</strong>
                                        {activeCabinet?.name ? (
                                            <> (depuis <strong>{activeCabinet.name}</strong>)</>
                                        ) : null}{' '}
                                        vers un autre cabinet.
                                    </p>
                                    <p className="hono-modal-helper" style={{ marginTop: '0.35rem' }}>
                                        Tout le dossier suit le client : charges, opérations, factures,
                                        tickets et historique.
                                    </p>
                                    {transferError ? (
                                        <div className="hono-modal-alert" role="alert">
                                            {transferError}
                                        </div>
                                    ) : null}
                                    {transferLoading ? (
                                        <p className="hono-modal-helper">Chargement des cabinets…</p>
                                    ) : transferCabinets.length === 0 ? (
                                        <p className="hono-modal-helper">
                                            Aucun autre cabinet trouvé. Créez un 2ᵉ cabinet dans
                                            « Gérer cabinet ».
                                        </p>
                                    ) : (
                                        <div className="client-transfer-list">
                                            {transferCabinets.map((cab) => {
                                                const isSelected = Number(selectedTargetCabinetId) === Number(cab.id);
                                                return (
                                                    <div
                                                        key={cab.id}
                                                        className={`client-transfer-item ${isSelected ? 'client-transfer-item--selected' : ''}`}
                                                        onClick={() => !transferBusy && setSelectedTargetCabinetId(cab.id)}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                if (!transferBusy) setSelectedTargetCabinetId(cab.id);
                                                            }
                                                        }}
                                                    >
                                                        <span className="client-transfer-item__name">
                                                            {cab.name}
                                                        </span>
                                                        <span className="client-transfer-item__meta">
                                                            {cab.code}
                                                            {Number(cab.is_default) === 1
                                                                ? ' · Données actuelles'
                                                                : ' · Nouveau'}
                                                        </span>
                                                        {isSelected ? (
                                                            <i className="fas fa-check-circle client-transfer-item__check" aria-hidden="true" />
                                                        ) : (
                                                            <i className="far fa-circle text-muted" aria-hidden="true" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="hono-modal-footer" style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        className="client-confirm-btn client-confirm-btn--cancel"
                                        onClick={() => setTransferClient(null)}
                                        disabled={transferBusy}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        className="client-confirm-btn client-confirm-btn--validate"
                                        onClick={handleTransferSubmit}
                                        disabled={transferBusy || !selectedTargetCabinetId || transferCabinets.length === 0}
                                    >
                                        {transferBusy ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin me-1" /> Déplacement…
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-check me-1" /> Valider
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {personnelModal && (
                        <div
                            className="modal-overlay hono-modal-overlay"
                            role="presentation"
                            onClick={closePersonnelModal}
                        >
                            <div
                                className="hono-modal-plaque personnel-modal-plaque"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="personnel-modal-title"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <form onSubmit={handlePersonnelSubmit}>
                                    <div className="hono-modal-header">
                                        <div id="personnel-modal-title" className="hono-modal-title">
                                            Info personnel — {personnelModal.client?.nom || '…'}
                                        </div>
                                    </div>
                                    <div className="hono-modal-body">
                                        <div className="hono-modal-helper">
                                            Liez la société aux informations de contact, du manager et de son identité.
                                        </div>
                                        {personnelError && (
                                            <div className="hono-modal-alert" role="alert">{personnelError}</div>
                                        )}
                                        {personnelLoading ? (
                                            <p className="hono-modal-helper">Chargement…</p>
                                        ) : (
                                            <>
                                                <div className="personnel-modal-section">Contact (contacts_info)</div>
                                                <div className="personnel-modal-grid">
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-admin-email">
                                                            E-mail administration
                                                        </label>
                                                        <input
                                                            id="personnel-admin-email"
                                                            type="email"
                                                            className="hono-modal-input"
                                                            value={personnelForm.administration_email}
                                                            onChange={(e) => handlePersonnelFormChange('administration_email', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-admin-phone">
                                                            Téléphone administration
                                                        </label>
                                                        <input
                                                            id="personnel-admin-phone"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.administration_phone_number}
                                                            onChange={(e) => handlePersonnelFormChange('administration_phone_number', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="personnel-modal-section">Manager (managers_info)</div>
                                                <div className="personnel-modal-grid">
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-first-name">
                                                            Prénom
                                                        </label>
                                                        <input
                                                            id="personnel-first-name"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.first_name}
                                                            onChange={(e) => handlePersonnelFormChange('first_name', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-middle-name">
                                                            Nom du milieu
                                                        </label>
                                                        <input
                                                            id="personnel-middle-name"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.middle_name}
                                                            onChange={(e) => handlePersonnelFormChange('middle_name', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-last-name">
                                                            Nom
                                                        </label>
                                                        <input
                                                            id="personnel-last-name"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.last_name}
                                                            onChange={(e) => handlePersonnelFormChange('last_name', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-birth-date">
                                                            Date de naissance
                                                        </label>
                                                        <input
                                                            id="personnel-birth-date"
                                                            type="date"
                                                            className="hono-modal-input"
                                                            value={personnelForm.birth_date}
                                                            onChange={(e) => handlePersonnelFormChange('birth_date', e.target.value)}
                                                            disabled={personnelSaving}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-mgr-phones">
                                                            Téléphones manager
                                                        </label>
                                                        <input
                                                            id="personnel-mgr-phones"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.phone_numbers}
                                                            onChange={(e) => handlePersonnelFormChange('phone_numbers', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-mgr-emails">
                                                            E-mails manager
                                                        </label>
                                                        <input
                                                            id="personnel-mgr-emails"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.emails}
                                                            onChange={(e) => handlePersonnelFormChange('emails', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="personnel-modal-section">Identité (identities_info)</div>
                                                <div className="personnel-modal-grid">
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-id-type">
                                                            Type
                                                        </label>
                                                        <select
                                                            id="personnel-id-type"
                                                            className="hono-modal-input"
                                                            value={personnelForm.identity_type}
                                                            onChange={(e) => handlePersonnelFormChange('identity_type', e.target.value)}
                                                            disabled={personnelSaving}
                                                        >
                                                            <option value="">—</option>
                                                            <option value="NIC">CIN / NIC</option>
                                                            <option value="Passport">Passeport</option>
                                                            <option value="Other">Autre</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-id-number">
                                                            N° identité
                                                        </label>
                                                        <input
                                                            id="personnel-id-number"
                                                            type="text"
                                                            className="hono-modal-input"
                                                            value={personnelForm.identity_number}
                                                            onChange={(e) => handlePersonnelFormChange('identity_number', e.target.value)}
                                                            disabled={personnelSaving}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-id-issue">
                                                            Date délivrance
                                                        </label>
                                                        <input
                                                            id="personnel-id-issue"
                                                            type="date"
                                                            className="hono-modal-input"
                                                            value={personnelForm.identity_issue_date}
                                                            onChange={(e) => handlePersonnelFormChange('identity_issue_date', e.target.value)}
                                                            disabled={personnelSaving}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="hono-modal-label" htmlFor="personnel-id-expiry">
                                                            Date expiration
                                                        </label>
                                                        <input
                                                            id="personnel-id-expiry"
                                                            type="date"
                                                            className="hono-modal-input"
                                                            value={personnelForm.identity_expiray_date}
                                                            onChange={(e) => handlePersonnelFormChange('identity_expiray_date', e.target.value)}
                                                            disabled={personnelSaving}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="personnel-modal-actions">
                                            <button
                                                type="button"
                                                className="personnel-modal-btn personnel-modal-btn--cancel"
                                                onClick={closePersonnelModal}
                                                disabled={personnelSaving}
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                type="submit"
                                                className="personnel-modal-btn personnel-modal-btn--save"
                                                disabled={personnelSaving || personnelLoading}
                                            >
                                                {personnelSaving ? 'Enregistrement…' : 'Enregistrer'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Formulaire d'ajout - Modal */}
                    {showAddForm && (
                        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
                            <div className="form-container" onClick={(e) => e.stopPropagation()}>
                                <h5 className="form-title">
                                    <i className="fas fa-user-plus me-2" aria-hidden="true" />
                                    Ajouter un client
                                </h5>
                                <ClientAddForm
                                    cabinetId={activeCabinet?.id ?? null}
                                    onClientAdded={() => {
                                        setShowAddForm(false);
                                        setSuccessMessage('Client ajouté avec succès !');
                                        fetchAllClients();
                                        setTimeout(() => setSuccessMessage(''), 3000);
                                    }}
                                    onCancel={() => setShowAddForm(false)}
                                />
                            </div>
                        </div>
                    )}

                    {honoModal && (
                        <div
                            className="modal-overlay hono-modal-overlay"
                            role="presentation"
                            onClick={closeHonoModal}
                        >
                            <div
                                className="hono-modal-plaque"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="hono-modal-title"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <form onSubmit={handleHonoModalSubmit}>
                                    <div className="hono-modal-header">
                                        <div id="hono-modal-title" className="hono-modal-title">
                                            Hono de {getClientNomComplet(honoModal.client) || '…'}
                                        </div>
                                    </div>
                                    <div className="hono-modal-body">
                                        <div className="hono-modal-helper">
                                            {honoModalLocked
                                                ? 'Honoraires enregistrés — consultation uniquement (modification réservée à l\'administrateur).'
                                                : 'Choisissez le type, le montant puis la date de début d\'application.'}
                                        </div>
                                        {honoModalLocked && (
                                            <div className="hono-modal-readonly-notice" role="status">
                                                Ce client a déjà un honoraire configuré. Seul un compte administrateur peut le modifier.
                                            </div>
                                        )}
                                        <div className="hono-modal-section-title">Type d'hono</div>
                                        <div className="hono-modal-radio-group" role="radiogroup" aria-label="Type d'hono">
                                            {Object.entries(PERIODE_LABELS).map(([key, label]) => (
                                                <label key={key} className={`hono-modal-radio-item${honoModalLocked ? ' is-locked' : ''}${honoArretChecked ? ' is-locked' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name="hono-periode"
                                                        value={key}
                                                        checked={selectedPeriodeModal === key}
                                                        onChange={() => handlePeriodeModalChange(key)}
                                                        disabled={honoModalSaving || honoModalLocked || honoArretChecked}
                                                    />
                                                    <span>{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <label className={`hono-modal-arret-label${honoArretChecked ? ' is-active' : ''}${honoModalLocked ? ' is-locked' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={honoArretChecked}
                                                onChange={(e) => {
                                                    if (honoModalLocked) return;
                                                    setHonoArretChecked(e.target.checked);
                                                    if (honoModalError) setHonoModalError('');
                                                }}
                                                disabled={honoModalSaving || honoModalLocked || !selectedPeriodeModal}
                                            />
                                            <span className="hono-modal-arret-text">Arrêt d'hono</span>
                                            {honoArretChecked && (
                                                <i className="fas fa-ban hono-modal-arret-icon" aria-hidden="true" />
                                            )}
                                        </label>
                                        {honoArretChecked && (
                                            <>
                                                <label className="hono-modal-label hono-modal-label--arret" htmlFor="hono-stop-date-input">
                                                    Date d'arrêt
                                                </label>
                                                <input
                                                    id="hono-stop-date-input"
                                                    type="date"
                                                    className="hono-modal-input hono-modal-input--arret"
                                                    value={stopDateModalInput}
                                                    onChange={(e) => {
                                                        setStopDateModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                />
                                            </>
                                        )}
                                        <label className="hono-modal-label" htmlFor="hono-montant-input">
                                            Montant
                                        </label>
                                        <input
                                            id="hono-montant-input"
                                            type="text"
                                            inputMode="decimal"
                                            className={`hono-modal-input${honoArretChecked ? ' hono-modal-input--arret' : ''}`}
                                            placeholder="Montant"
                                            value={montantModalInput}
                                            onChange={(e) => {
                                                setMontantModalInput(e.target.value);
                                                if (honoModalError) setHonoModalError('');
                                            }}
                                            autoComplete="off"
                                            disabled={honoModalSaving || honoModalLocked || honoArretChecked}
                                            readOnly={honoModalLocked || honoArretChecked}
                                        />
                                        {selectedPeriodeModal === 'mensuel' && !honoArretChecked && (
                                            <>
                                                <label className="hono-modal-label" htmlFor="hono-mois-select">
                                                    Mois de début de l'hono
                                                </label>
                                                <select
                                                    id="hono-mois-select"
                                                    className="hono-modal-input"
                                                    value={moisModalInput}
                                                    onChange={(e) => {
                                                        setMoisModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                >
                                                    {MOIS_HONO_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <label className="hono-modal-label" htmlFor="hono-annee-mensuel-input">
                                                    Année d'application
                                                </label>
                                                <input
                                                    id="hono-annee-mensuel-input"
                                                    type="number"
                                                    min="2000"
                                                    max="2100"
                                                    className="hono-modal-input"
                                                    value={anneeModalInput}
                                                    onChange={(e) => {
                                                        setAnneeModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                    readOnly={honoModalLocked}
                                                />
                                            </>
                                        )}
                                        {selectedPeriodeModal === 'trimestriel' && !honoArretChecked && (
                                            <>
                                                <label className="hono-modal-label" htmlFor="hono-trimestre-select">
                                                    Trimestre
                                                </label>
                                                <select
                                                    id="hono-trimestre-select"
                                                    className="hono-modal-input"
                                                    value={trimestreModalInput}
                                                    onChange={(e) => {
                                                        setTrimestreModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                >
                                                    <option value="1">1er trimestre</option>
                                                    <option value="2">2ème trimestre</option>
                                                    <option value="3">3ème trimestre</option>
                                                    <option value="4">4ème trimestre</option>
                                                </select>
                                                <label className="hono-modal-label" htmlFor="hono-annee-trimestre-input">
                                                    Année d'application
                                                </label>
                                                <input
                                                    id="hono-annee-trimestre-input"
                                                    type="number"
                                                    min="2000"
                                                    max="2100"
                                                    className="hono-modal-input"
                                                    value={anneeModalInput}
                                                    onChange={(e) => {
                                                        setAnneeModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                    readOnly={honoModalLocked}
                                                />
                                            </>
                                        )}
                                        {selectedPeriodeModal === 'semestriel' && !honoArretChecked && (
                                            <>
                                                <label className="hono-modal-label" htmlFor="hono-semestre-select">
                                                    Semestre
                                                </label>
                                                <select
                                                    id="hono-semestre-select"
                                                    className="hono-modal-input"
                                                    value={semestreModalInput}
                                                    onChange={(e) => {
                                                        setSemestreModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                >
                                                    <option value="1">1er semestre</option>
                                                    <option value="2">2ème semestre</option>
                                                </select>
                                                <label className="hono-modal-label" htmlFor="hono-annee-semestre-input">
                                                    Année d'application
                                                </label>
                                                <input
                                                    id="hono-annee-semestre-input"
                                                    type="number"
                                                    min="2000"
                                                    max="2100"
                                                    className="hono-modal-input"
                                                    value={anneeModalInput}
                                                    onChange={(e) => {
                                                        setAnneeModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                    readOnly={honoModalLocked}
                                                />
                                            </>
                                        )}
                                        {selectedPeriodeModal === 'annuel' && !honoArretChecked && (
                                            <>
                                                <label className="hono-modal-label" htmlFor="hono-annee-input">
                                                    Année
                                                </label>
                                                <input
                                                    id="hono-annee-input"
                                                    type="number"
                                                    min="2000"
                                                    max="2100"
                                                    className="hono-modal-input"
                                                    value={anneeModalInput}
                                                    onChange={(e) => {
                                                        setAnneeModalInput(e.target.value);
                                                        if (honoModalError) setHonoModalError('');
                                                    }}
                                                    disabled={honoModalSaving || honoModalLocked}
                                                    readOnly={honoModalLocked}
                                                />
                                            </>
                                        )}
                                        {honoModalError && (
                                            <div className="hono-modal-alert" role="alert">
                                                {honoModalError}
                                            </div>
                                        )}
                                    </div>
                                    <div className="hono-modal-footer">
                                        <div className="hono-modal-actions">
                                            <button
                                                type="button"
                                                className="hono-btn-modal-annuler"
                                                onClick={closeHonoModal}
                                                disabled={honoModalSaving}
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                type="button"
                                                className="hono-btn-modal-renitialiser"
                                                onClick={resetHonoModalForm}
                                                disabled={honoModalSaving || honoModalLocked}
                                            >
                                                Réinitialiser
                                            </button>
                                            <button
                                                type="submit"
                                                className={`hono-btn-modal-valider${honoArretChecked ? ' hono-btn-modal-valider--arret' : ''}`}
                                                disabled={honoModalSaving || honoModalLocked}
                                            >
                                                {honoModalSaving ? '…' : honoModalLocked ? 'Verrouillé' : honoArretChecked ? 'Confirmer arrêt' : 'Valider'}
                                            </button>
                                        </div>
                                        {userIsAdmin && periodeParClient[honoModal.client.id] && (
                                            <button
                                                type="button"
                                                className="hono-modal-unlock"
                                                onClick={() => {
                                                    unlockClientPeriodes(honoModal.client.id);
                                                    closeHonoModal();
                                                }}
                                            >
                                                Déverrouiller les autres périodes
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Formulaire de modification */}
                    {editMode && editingClient && (
                        <div
                            className="modal-overlay"
                            onClick={() => {
                                setEditMode(false);
                                setEditingClient(null);
                            }}
                        >
                            <div className="form-container" onClick={(e) => e.stopPropagation()}>
                                <h5 className="form-title">
                                    <i className="fas fa-user-edit me-2" aria-hidden="true" />
                                    Modifier le client
                                </h5>
                                <ClientEditForm
                                    client={editingClient}
                                    nhLocked={isClientNhTypeLocked(editingClient)}
                                    userIsAdmin={userIsAdmin}
                                    onClientUpdated={() => {
                                        setEditMode(false);
                                        setEditingClient(null);
                                        setSuccessMessage('Client modifié avec succès !');
                                        fetchAllClients();
                                        setTimeout(() => setSuccessMessage(''), 3000);
                                    }}
                                    onCancel={() => {
                                        setEditMode(false);
                                        setEditingClient(null);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Affichage conditionnel : Boutons ou Contenu */}
                    {!showContent ? (
                        // Afficher les deux boutons
                        <div className="dashboard-buttons-container">
                            <h2 style={{ color: '#000000', marginBottom: '2rem', fontSize: '2rem', textAlign: 'center' }}>
                                Sélectionnez une option
                            </h2>
                            <div className="dashboard-buttons-grid">
                                <button
                                    className="dashboard-button-item cgm-button"
                                    onClick={() => setShowContent(true)}
                                >
                                    <div className="button-icon">👥</div>
                                    <div className="button-text" >Gestion de Caisse – CGM</div>
                                </button>

                                <button
                                    className="dashboard-button-item nh-button"
                                    onClick={() => navigate('/nh', { state: { pickMatricule: true } })}
                                >
                                    <div className="button-icon">🧾</div>
                                    <div className="button-text">
                                        Gestion des Notes d&apos;Honoraires
                                        <br />
                                        – CGM
                                    </div>
                                </button>
                            </div>
                        </div>
                    ) : (
                        // Afficher le contenu (front de caisse — test)
                        <>
                            <div className="clients-pos-view">
                                <div className="clients-pos-top">
                                    <p className="clients-pos-crumb">Gestion caisse / Honoraires par période</p>
                                    <div className="clients-page-topbar">
                                        <div className="clients-page-topbar__left">
                                            <button
                                                type="button"
                                                className="cgm-toolbar-btn cgm-toolbar-btn--back"
                                                onClick={() => navigate('/')}
                                            >
                                                <span className="go-back-icon-inline" aria-hidden="true">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" aria-hidden="true">
                                                        <path d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z" />
                                                        <path d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z" />
                                                    </svg>
                                                </span>
                                                Retour
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="clients-pos-centered">
                                    <div className="cgm-caisse-year-banner" role="group" aria-label="Année d'activation de la caisse">
                                        <div className="cgm-caisse-year-banner__text">
                                            <span className="cgm-caisse-year-banner__eyebrow">Exercice caisse</span>
                                            <strong className="cgm-caisse-year-banner__title">Année d&apos;activation</strong>
                                            <span className="cgm-caisse-year-banner__hint">
                                                {activeCaisseYear === getCalendarYear()
                                                    ? `Année en cours · démarrage ${getCaisseStartYear()}`
                                                    : `Consultation ${activeCaisseYear} · calendrier ${getCalendarYear()}`}
                                            </span>
                                        </div>
                                        <label className="cgm-caisse-year-banner__select-wrap" htmlFor="caisse-active-year">
                                            <span className="cgm-sr-only">Choisir l&apos;année d&apos;activation</span>
                                            <select
                                                id="caisse-active-year"
                                                className="cgm-caisse-year-banner__select"
                                                value={activeCaisseYear}
                                                onChange={handleCaisseYearChange}
                                            >
                                                {caisseYearOptions.map((y) => (
                                                    <option key={y} value={y}>
                                                        {y}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                    <div className="cgm-list-toolbar">
                                        <div className="cgm-filter-panel">
                                            <div className="cgm-filter-panel__header">
                                                <div className="cgm-filter-panel__title">
                                                    {showArchives ? 'Clients archivés' : 'Critères de recherche'}
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`cgm-toolbar-btn${showArchives ? ' cgm-toolbar-btn--reset' : ' cgm-toolbar-btn--archives'}`}
                                                    onClick={() => setShowArchives((v) => !v)}
                                                    title={showArchives ? 'Retour à la liste des clients' : 'Voir les clients archivés'}
                                                >
                                                    <i className={`fas ${showArchives ? 'fa-arrow-left' : 'fa-archive'}`} aria-hidden="true" />
                                                    {showArchives ? 'Liste des clients' : 'Liste des archives'}
                                                </button>
                                            </div>
                                            <div className="cgm-filter-row">
                                                <div className="cgm-filter-search">
                                                    <label className="cgm-sr-only" htmlFor="clients-pos-search-input">
                                                        Recherche
                                                    </label>
                                                    <div className="cgm-filter-search__box" role="search">
                                                        <div className="cgm-filter-search__icon" aria-hidden="true">
                                                            <svg viewBox="0 0 20 20">
                                                                <path d="M16.72 17.78a.75.75 0 1 0 1.06-1.06l-1.06 1.06ZM9 14.5A5.5 5.5 0 0 1 3.5 9H2a7 7 0 0 0 7 7v-1.5ZM3.5 9A5.5 5.5 0 0 1 9 3.5V2a7 7 0 0 0-7 7h1.5ZM9 3.5A5.5 5.5 0 0 1 14.5 9H16a7 7 0 0 0-7-7v1.5Zm3.89 10.45 3.83 3.83 1.06-1.06-3.83-3.83-1.06 1.06ZM14.5 9a5.48 5.48 0 0 1-1.61 3.89l1.06 1.06A6.98 6.98 0 0 0 16 9h-1.5Zm-1.61 3.89A5.48 5.48 0 0 1 9 14.5V16a6.98 6.98 0 0 0 4.95-2.05l-1.06-1.06Z" />
                                                            </svg>
                                                        </div>
                                                        <input
                                                            id="clients-pos-search-input"
                                                            type="search"
                                                            className="cgm-filter-search__input"
                                                            placeholder="ID, nom, adresse, téléphone…"
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            autoComplete="off"
                                                            aria-label="Rechercher un client"
                                                        />
                                                        {searchTerm ? (
                                                            <button
                                                                type="button"
                                                                className="cgm-filter-search__clear"
                                                                onClick={() => setSearchTerm('')}
                                                                aria-label="Effacer la recherche"
                                                            >
                                                                <i className="fas fa-times" aria-hidden="true" />
                                                            </button>
                                                        ) : null}
                                                        <button type="button" className="cgm-filter-search__submit" tabIndex={-1}>
                                                            Filtrer
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="cgm-filter-field">
                                                    <label htmlFor="filter-nh-type">NH / E</label>
                                                    <select
                                                        id="filter-nh-type"
                                                        value={filterNhType}
                                                        onChange={(e) => setFilterNhType(e.target.value)}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="NH">NH</option>
                                                        <option value="E">E</option>
                                                    </select>
                                                </div>
                                                <div className="cgm-filter-field">
                                                    <label htmlFor="filter-periode">Type hono</label>
                                                    <select
                                                        id="filter-periode"
                                                        value={filterPeriode}
                                                        onChange={(e) => setFilterPeriode(e.target.value)}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="mensuel">Mensuel</option>
                                                        <option value="trimestriel">Trimestriel</option>
                                                        <option value="semestriel">Semestriel</option>
                                                        <option value="annuel">Annuel</option>
                                                    </select>
                                                </div>
                                                                                     <div className="cgm-filter-field">
                                                    <label htmlFor="filter-personne">P.P / P.M</label>
                                                    <select
                                                        id="filter-personne"
                                                        value={filterPersonneType}
                                                        onChange={(e) => setFilterPersonneType(e.target.value)}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="physique">P.P</option>
                                                        <option value="morale">P.M</option>
                                                    </select>
                                                </div>
                                                {hasActiveClientFilters && (
                                                    <button
                                                        type="button"
                                                        className="cgm-toolbar-btn cgm-toolbar-btn--reset"
                                                        onClick={() => {
                                                            setFilterNhType('');
                                                            setFilterPeriode('');
                                                            setFilterPersonneType('');
                                                        }}
                                                    >
                                                        <i className="fas fa-undo" aria-hidden="true" />
                                                        Rétablir
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="nh-modern-table-card clients-modern-table-card">
                                        {loading ? (
                                            <div className="text-center" style={{ padding: '2rem 1rem' }}>
                                                <div className="modern-spinner"></div>
                                                <p>Chargement des clients...</p>
                                            </div>
                                        ) : (!showArchives || filteredClients.length > 0) ? (
                                            <>
                                                <div className="nh-modern-table-toolbar clients-table-toolbar">
                                                    <div className="clients-table-toolbar__left">
                                                        <label className="nh-modern-table-length" htmlFor="clients-page-size">
                                                            Afficher
                                                            <select
                                                                id="clients-page-size"
                                                                value={itemsPerPage}
                                                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                                            >
                                                                <option value={10}>10</option>
                                                                <option value={25}>25</option>
                                                                <option value={50}>50</option>
                                                                <option value={100}>100</option>
                                                            </select>
                                                            lignes
                                                        </label>
                                                        <span className="clients-table-count">
                                                            <span className="cgm-count-badge">{totalClientsCount}</span>
                                                            <span className="clients-table-count__label">
                                                                {showArchives ? 'archivé' : 'client'}
                                                                {totalClientsCount !== 1 ? 's' : ''}
                                                                {hasActiveClientFilters ? (
                                                                    <>
                                                                        {' '}
                                                                        · <strong>{visibleClientsCount}</strong> affiché{visibleClientsCount !== 1 ? 's' : ''}
                                                                    </>
                                                                ) : null}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="clients-table-toolbar__actions">
                                                        <button
                                                            type="button"
                                                            className={`cgm-toolbar-btn${showArchives ? ' cgm-toolbar-btn--reset' : ' cgm-toolbar-btn--archives'}`}
                                                            onClick={() => setShowArchives((v) => !v)}
                                                            title={showArchives ? 'Retour à la liste des clients' : 'Voir les clients archivés'}
                                                        >
                                                            <i className={`fas ${showArchives ? 'fa-arrow-left' : 'fa-archive'}`} aria-hidden="true" />
                                                            {showArchives ? 'Liste des clients' : 'Liste des archives'}
                                                        </button>
                                                        {!showArchives ? (
                                                        <button
                                                            type="button"
                                                            className="cgm-toolbar-btn cgm-toolbar-btn--add"
                                                            onClick={() => {
                                                                setShowAddForm(true);
                                                                setEditMode(false);
                                                                setDeleteMode(false);
                                                            }}
                                                        >
                                                            <i className="fas fa-plus" aria-hidden="true" />
                                                            Ajouter un client
                                                        </button>
                                                        ) : null}
                                                        <button
                                                            type="button"
                                                            className="cgm-toolbar-btn cgm-toolbar-btn--print"
                                                            disabled={filteredClients.length === 0}
                                                            onClick={handlePrintClientsHonoraires}
                                                            title="Imprimer la liste"
                                                        >
                                                            <i className="fas fa-print" aria-hidden="true" />
                                                            Imprimer
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="overview-table-container nh-modern-table-scroll">
                                                    <table className="overview-table nh-overview-data-table nh-modern-data-table">
                                                        <thead>
                                                            <tr>
                                                                {isCabinetAdmin ? (
                                                                    <th className="col-client-transfer" aria-label="Transfert cabinet">
                                                                        <span className="nh-modern-th-inner" />
                                                                    </th>
                                                                ) : null}
                                                                <th className="col-client-chrono">
                                                                    <span className="nh-modern-th-inner">
                                                                        N° chrono
                                                                        <span className="nh-modern-sort-icons" aria-hidden="true">
                                                                            <i className="fas fa-caret-up" />
                                                                            <i className="fas fa-caret-down" />
                                                                        </span>
                                                                    </span>
                                                                </th>
                                                                <th className="col-client-nom">
                                                                    <span className="nh-modern-th-inner">
                                                                        Nom
                                                                        <span className="nh-modern-sort-icons" aria-hidden="true">
                                                                            <i className="fas fa-caret-up" />
                                                                            <i className="fas fa-caret-down" />
                                                                        </span>
                                                                    </span>
                                                                </th>
                                                                <th className="col-client-hono-action">
                                                                    <span className="nh-modern-th-inner">Honoraires</span>
                                                                </th>
                                                                <th className="col-client-hono-montant">
                                                                    <span className="nh-modern-th-inner">
                                                                        Montant honoraire
                                                                        <span className="nh-modern-sort-icons" aria-hidden="true">
                                                                            <i className="fas fa-caret-up" />
                                                                            <i className="fas fa-caret-down" />
                                                                        </span>
                                                                    </span>
                                                                </th>
                                                                <th className="col-client-hono-type">
                                                                    <span className="nh-modern-th-inner">
                                                                        Type honoraire
                                                                        <span className="nh-modern-sort-icons" aria-hidden="true">
                                                                            <i className="fas fa-caret-up" />
                                                                            <i className="fas fa-caret-down" />
                                                                        </span>
                                                                    </span>
                                                                </th>
                                                                <th className="col-client-hono-start">
                                                                    <span className="nh-modern-th-inner">
                                                                        Date début
                                                                    </span>
                                                                </th>
                                                                <th className="col-client-hono-delete nh-col-actions">
                                                                    <span className="nh-modern-th-inner">Action</span>
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentPageClients.map((client) => {
                                                                const choixPeriode = periodeParClient[client.id];
                                                                const montantCentral = getMontantHonoCentral(client, choixPeriode);
                                                                const typeHonoCentral = getTypeHonoCentral(client, choixPeriode);
                                                                const startDateCentral = getStartDateHonoCentral(client, choixPeriode);
                                                                const isHonoStopped = !!stoppedParClient[client.id]?.stopped;
                                                                const isSelected = selectedClient?.id === client.id;
                                                                const goToClientCharges = (e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/client/${client.id}/charges`);
                                                                };
                                                                const handleClientCellKeyDown = (e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        navigate(`/client/${client.id}/charges`);
                                                                    }
                                                                };
                                                                const openClientDetails = (e) => {
                                                                    e.stopPropagation();
                                                                    setDetailsClient({
                                                                        client,
                                                                        personneType: personneTypeParClient[client.id],
                                                                        nhType: nhTypeParClient[client.id] || 'E',
                                                                        montantHono: montantCentral,
                                                                        typeHono: typeHonoCentral
                                                                    });
                                                                };
                                                                return (
                                                                    <tr
                                                                        key={client.id}
                                                                        className={`clickable-row${isSelected ? ' nh-row-selected' : ''}`}
                                                                        onClick={() => setSelectedClient(isSelected ? null : client)}
                                                                    >
                                                                        {isCabinetAdmin ? (
                                                                            <td
                                                                                className="col-client-transfer"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    className="nh-action-icon-btn nh-action-icon-btn--transfer"
                                                                                    title="Envoyer infos personnelles vers un autre cabinet"
                                                                                    aria-label="Envoyer vers un cabinet"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        openTransferModal(client);
                                                                                    }}
                                                                                >
                                                                                    <svg
                                                                                        width="16"
                                                                                        height="16"
                                                                                        viewBox="0 0 24 24"
                                                                                        fill="none"
                                                                                        stroke="currentColor"
                                                                                        strokeWidth="2.25"
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        aria-hidden="true"
                                                                                    >
                                                                                        <line x1="5" y1="12" x2="19" y2="12" />
                                                                                        <polyline points="12 5 19 12 12 19" />
                                                                                    </svg>
                                                                                </button>
                                                                            </td>
                                                                        ) : null}
                                                                        <td className="col-client-chrono">
                                                                            {client.num_chrono != null && client.num_chrono !== ''
                                                                                ? client.num_chrono
                                                                                : '—'}
                                                                        </td>
                                                                        <td
                                                                            className="col-client-nom clickable-client-cell"
                                                                            onClick={goToClientCharges}
                                                                            onDoubleClick={goToClientCharges}
                                                                            onKeyDown={handleClientCellKeyDown}
                                                                            tabIndex={0}
                                                                            role="button"
                                                                            title={`${client.nom || ''} — cliquer pour ouvrir les charges`}
                                                                        >
                                                                            {client.nom || '—'}
                                                                        </td>
                                                                        <td className="col-client-hono-action">
                                                                            <div className="hono-cell-inner">
                                                                                <button
                                                                                    type="button"
                                                                                    className="clients-hono-manage-btn"
                                                                                    title="Gérer le type d'hono et le montant"
                                                                                    onClick={(e) => openHonoModal(e, client)}
                                                                                >
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                                                        <path d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.07 7.07 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 2h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 8.44a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.4.31.6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54c.05.24.25.42.49.42h3.8c.24 0 .44-.18.49-.42l.36-2.54c.58-.23 1.12-.54 1.63-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5z" />
                                                                                    </svg>
                                                                                    Gérer
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                        <td className="col-client-hono-montant">
                                                                            {montantCentral ? (
                                                                                <span
                                                                                    className="nh-badge-amount"
                                                                                    style={isHonoStopped ? { opacity: 0.45, textDecoration: 'line-through' } : undefined}
                                                                                >
                                                                                    {montantCentral}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="col-client-hono-type">
                                                                            {typeHonoCentral ? (
                                                                                <span
                                                                                    className="nh-badge-taux"
                                                                                    style={isHonoStopped ? { color: '#e65100', background: '#fff7ed' } : undefined}
                                                                                >
                                                                                    {typeHonoCentral}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="col-client-hono-start">
                                                                            {startDateCentral ? (
                                                                                <span
                                                                                    className="clients-hono-start-date"
                                                                                    style={isHonoStopped ? { opacity: 0.45 } : undefined}
                                                                                >
                                                                                    {startDateCentral}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td
                                                                            className="col-client-hono-delete nh-col-actions"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <div className="nh-action-icons nh-action-icons--split">
                                                                                <button
                                                                                    type="button"
                                                                                    className="nh-action-icon-btn nh-action-icon-btn--personnel"
                                                                                    title="Info personnel (contact & manager)"
                                                                                    aria-label="Info personnel"
                                                                                    onClick={(e) => openPersonnelModal(e, client)}
                                                                                >
                                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                                                        <circle cx="12" cy="7" r="4" />
                                                                                    </svg>
                                                                                    Perso
                                                                                </button>
                                                                                <div className="nh-action-icons-end">
                                                                                <button
                                                                                    type="button"
                                                                                    className="nh-action-icon-btn nh-action-icon-btn--view"
                                                                                    title="Voir le client"
                                                                                    aria-label="Voir le client"
                                                                                    onClick={openClientDetails}
                                                                                >
                                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                                        <circle cx="12" cy="12" r="3" />
                                                                                    </svg>
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="nh-action-icon-btn nh-action-icon-btn--edit"
                                                                                    title="Modifier le client"
                                                                                    aria-label="Modifier le client"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleEditClient(client);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                                    </svg>
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className={`nh-action-icon-btn ${showArchives ? 'nh-action-icon-btn--unarchive' : 'nh-action-icon-btn--archive'}`}
                                                                                    title={showArchives ? 'Désarchiver le client' : 'Archiver le client'}
                                                                                    aria-label={showArchives ? 'Désarchiver le client' : 'Archiver le client'}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        openArchiveConfirm(client, !showArchives);
                                                                                    }}
                                                                                >
                                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                                        <polyline points="21 8 21 21 3 21 3 8" />
                                                                                        <rect x="1" y="3" width="22" height="5" />
                                                                                        <line x1="10" y1="12" x2="14" y2="12" />
                                                                                    </svg>
                                                                                    {showArchives ? 'Désarchiver' : 'Archiver'}
                                                                                </button>
                                                                                {userIsAdmin && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="nh-action-icon-btn nh-action-icon-btn--delete"
                                                                                    title="Supprimer le client"
                                                                                    aria-label="Supprimer le client"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        openDeleteConfirm(client);
                                                                                        if (selectedClient?.id === client.id) {
                                                                                            setSelectedClient(null);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                                        <polyline points="3 6 5 6 21 6" />
                                                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                                                        <line x1="10" y1="11" x2="10" y2="17" />
                                                                                        <line x1="14" y1="11" x2="14" y2="17" />
                                                                                    </svg>
                                                                                </button>
                                                                                )}
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div className="nh-modern-table-footer">

                                                    <div className="nh-modern-pagination">
                                                        <button
                                                            type="button"
                                                            className="nh-modern-page-nav"
                                                            onClick={goToPrevPage}
                                                            disabled={currentPage === 1}
                                                        >
                                                            Précédent
                                                        </button>
                                                        <div className="nh-modern-page-numbers">
                                                            {getClientsPageNumbers(currentPage, totalPages).map((page, index) => (
                                                                page === '...' ? (
                                                                    <span key={`dots-${index}`} className="nh-modern-page-dots">…</span>
                                                                ) : (
                                                                    <button
                                                                        key={`page-${page}`}
                                                                        type="button"
                                                                        className={`nh-modern-page-btn${currentPage === page ? ' is-active' : ''}`}
                                                                        onClick={() => setCurrentPage(page)}
                                                                    >
                                                                        {page}
                                                                    </button>
                                                                )
                                                            ))}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="nh-modern-page-nav"
                                                            onClick={goToNextPage}
                                                            disabled={currentPage === totalPages}
                                                        >
                                                            Suivant
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="modern-alert modern-alert-info" style={{ margin: '1rem' }}>
                                                <div className="text-center">
                                                    <i className="fas fa-info-circle fa-3x mb-3 text-info"></i>
                                                    <h6 className="mb-2">Aucun client trouvé</h6>
                                                    <p className="mb-0">
                                                        {searchTerm || hasActiveClientFilters
                                                            ? 'Aucun client ne correspond à vos critères.'
                                                            : 'Aucun client enregistré dans le système.'}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );

}

